import {
  ArrowUpRight,
  Camera,
  Check,
  Copy,
  Eye,
  Facebook,
  Filter,
  Frame,
  Images,
  Instagram,
  MapPin,
  MessageCircle,
  Move,
  PlayCircle,
  RotateCcw,
  ScanLine,
  Search,
  Send,
  Share2,
  ShoppingBag,
  Sparkles,
  X
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent, type PointerEvent as ReactPointerEvent } from 'react';
import pixelsCatalog from './data/pixelsCatalog.json';
import { marketplaceCapturedAt, marketplaceListings, type MarketplaceListing, type MarketplaceMedia } from './data/marketplace';

type PixelWork = {
  slug: string;
  title: string;
  width: number;
  height: number;
  printPrice: string | null;
  originalPrice: string | null;
  originalStatus?: 'sold';
  originalStatusNote?: string;
  pixelsUrl: string;
  imageAlt: string;
  imagePath: string;
  sourceImageUrl: string;
};

type ListingView = MarketplaceListing & {
  image: string;
  pixelsUrl?: string;
  printPrice?: string | null;
  matchedTitle?: string;
  media: MarketplaceMedia[];
};

type RoomViewState = {
  cameraRequested: boolean;
  listing: ListingView;
  mediaMode: 'artwork' | 'framed';
  wallGuideInches: number | null;
  x: number;
  y: number;
  scale: number;
  rotation: number;
};

type RoomPlacementState = Pick<RoomViewState, 'rotation' | 'scale' | 'x' | 'y'> & {
  wallGuideInches?: number | null;
};

type CameraStatus = 'idle' | 'starting' | 'live' | 'blocked';
type RoomArSupport = 'checking' | 'available' | 'unavailable';
type RoomArSessionStatus = 'idle' | 'starting' | 'placed' | 'failed';

type RoomArModelElement = HTMLElement & {
  activateAR?: () => Promise<void>;
  canActivateAR?: boolean;
  loaded?: boolean;
};

type LivingViewState = {
  listing: ListingView;
  unlockedAt: number;
};

type FullscreenArtViewState = {
  listing: ListingView;
  media: MarketplaceMedia;
};

type ArtworkDimensions = {
  heightInches: number;
  label: string;
  widthInches: number;
};

type ContactFormState = {
  artworkId: string;
  email: string;
  intent: string;
  message: string;
  name: string;
  phone: string;
  startedAt: number;
  timeline: string;
  website: string;
};

type ContactFormStatus = 'idle' | 'sending' | 'sent' | 'error';

const typedPixels = pixelsCatalog as PixelWork[];
// Optional Cloudflare Turnstile key. The Worker still protects the endpoint
// with honeypot, timing, validation, and KV rate limits when this is empty.
const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim() ?? '';

const formatPrice = (price: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);

const filters = ['All', 'Golden hour', 'Marsh', 'Coastal', 'Dock', 'XL', 'Small work'];
const availabilityFilters = [
  { id: 'all', label: 'All', note: 'Every public original', primary: false },
  { id: 'available', label: 'Available', note: 'Ready to ask about', primary: true },
  { id: 'pending', label: 'Pending', note: 'Buyer in motion', primary: false },
  { id: 'reserved', label: 'Reserved', note: 'Being held', primary: false },
  { id: 'sold', label: 'Sold', note: 'Recently missed', primary: false },
  { id: 'hidden', label: 'Not listed', note: 'Private or paused', primary: false }
] as const;
const contactIntentOptions = ['Availability', 'Room fit', 'Pickup or delivery', 'Shipping', 'Gift timing', 'Designer or project', 'I am not sure yet'];

type AvailabilityFilterId = (typeof availabilityFilters)[number]['id'];

function createContactFormState(): ContactFormState {
  return {
    artworkId: '',
    email: '',
    intent: contactIntentOptions[0],
    message: '',
    name: '',
    phone: '',
    // The Worker rejects instant submits; resetting this after a successful
    // note gives the next real submit a fresh human-read timing window.
    startedAt: Date.now(),
    timeline: '',
    // Hidden field for simple bot detection. It stays invisible to visitors.
    website: ''
  };
}

const defaultGalleryState = {
  activeAvailabilityId: 'available' as AvailabilityFilterId,
  activeFilter: 'All',
  query: ''
};

const roomPreviewReferenceWidthInches = 36;
const roomPreviewReferenceWidthVmin = 34;
const roomWallGuideOptions = [48, 60, 72, 96] as const;
const roomPlacementPresets = [
  { id: 'center', label: 'Centered wall', x: 50, y: 46, scale: 100 },
  { id: 'sofa', label: 'Over sofa', x: 50, y: 36, scale: 118 },
  { id: 'hallway', label: 'Hallway', x: 50, y: 48, scale: 82 },
  { id: 'statement', label: 'Statement', x: 50, y: 44, scale: 135 }
] as const;

function formatDimensionNumber(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, '');
}

function parseArtworkDimensions(value?: string): ArtworkDimensions | null {
  const match = String(value ?? '').match(/(\d+(?:\.\d+)?)\s*(?:x|×|by)\s*(\d+(?:\.\d+)?)/i);
  if (!match) return null;

  const widthInches = Number(match[1]);
  const heightInches = Number(match[2]);
  if (!Number.isFinite(widthInches) || !Number.isFinite(heightInches) || widthInches <= 0 || heightInches <= 0) return null;

  return {
    heightInches,
    label: `${formatDimensionNumber(widthInches)} x ${formatDimensionNumber(heightInches)}`,
    widthInches
  };
}

function listingDimensions(listing: ListingView) {
  return parseArtworkDimensions(listing.roomDimensions) ?? parseArtworkDimensions(listing.dimensions) ?? parseArtworkDimensions(listing.title);
}

function dimensionLabel(listing: ListingView) {
  if (listing.dimensions?.toLowerCase().startsWith('two ')) return listing.dimensions;
  const dimensions = parseArtworkDimensions(listing.dimensions) ?? listingDimensions(listing);
  return dimensions ? `${dimensions.label} in` : 'Size coming soon';
}

function buyerDimensionPhrase(listing: ListingView) {
  if (listing.dimensions?.toLowerCase().startsWith('two ')) return listing.dimensions;
  const dimensions = parseArtworkDimensions(listing.dimensions) ?? listingDimensions(listing);
  return dimensions ? `${dimensions.label} in` : 'Size coming soon';
}

function buyerDimensionSuffix(listing: ListingView) {
  return `, ${buyerDimensionPhrase(listing)}`;
}

function hasKnownDimensions(listing: ListingView) {
  return Boolean(listingDimensions(listing));
}

function roomArtworkMetrics(listing: ListingView, scale: number) {
  const dimensions = listingDimensions(listing);
  const normalizedScale = scale / 100;

  if (!dimensions) {
    return {
      aspectRatio: '4 / 3',
      displayWidth: `${clamp(scale * 0.38, 18, 64)}vmin`,
      dimensionsKnown: false,
      label: 'Size coming soon · preview shown for shape only',
      sizeClass: '',
      scaleLabel: 'Preview is for shape only until Leslie confirms the exact canvas size.'
    };
  }

  const displayWidthVmin = clamp((dimensions.widthInches / roomPreviewReferenceWidthInches) * roomPreviewReferenceWidthVmin * normalizedScale, 11, 74);
  const approxDisplayHeightVmin = displayWidthVmin * (dimensions.heightInches / dimensions.widthInches);

  return {
    aspectRatio: `${dimensions.widthInches} / ${dimensions.heightInches}`,
    dimensionsKnown: true,
    displayWidth: `${displayWidthVmin.toFixed(2)}vmin`,
    label: `${dimensions.label} in · preview scale ${Math.round(scale)}%`,
    scaleLabel: `Actual artwork size is ${dimensions.label} in. Use the scale slider only to match your camera distance.`,
    sizeClass: approxDisplayHeightVmin > 62 ? 'is-tall-preview' : ''
  };
}

function roomWallWidthGuide(listing: ListingView, scale: number, wallGuideInches: number | null) {
  const dimensions = listingDimensions(listing);
  if (!dimensions || !wallGuideInches) return null;

  const normalizedScale = scale / 100;
  const artworkWidthVmin = clamp((dimensions.widthInches / roomPreviewReferenceWidthInches) * roomPreviewReferenceWidthVmin * normalizedScale, 11, 74);
  const guideWidthVmin = clamp(artworkWidthVmin * (wallGuideInches / dimensions.widthInches), 30, 88);
  const percentOfGuide = Math.round((dimensions.widthInches / wallGuideInches) * 100);

  return {
    label: `${wallGuideInches} in guide`,
    note: `${dimensions.label} in painting is about ${percentOfGuide}% of that span.`,
    width: `${guideWidthVmin.toFixed(2)}vmin`
  };
}

function roomScaleConfidenceText(listing: ListingView) {
  const dimensions = listingDimensions(listing);
  if (!dimensions) {
    return {
      level: 'estimated',
      title: 'Shape preview only',
      note: 'Leslie has not listed the exact size for this piece yet, so this helps you judge the mood and shape rather than exact scale.'
    };
  }

  return {
    level: 'known',
    title: `${dimensions.label} in size used`,
    note: 'This preview uses the listed artwork dimensions. Camera distance and wall angle can still change how it feels in the room.'
  };
}

function roomWallFitFacts(listing: ListingView, mediaMode: RoomViewState['mediaMode']) {
  const dimensions = listingDimensions(listing);
  const hasFrame = roomViewUsesFramedImage(listing, mediaMode);

  if (!dimensions) {
    return [
      { label: 'Size', value: 'Coming soon', note: 'Shape preview only' },
      { label: 'Scale', value: 'Estimated', note: 'Use for mood and placement' },
      { label: 'View', value: hasFrame ? 'Framed view' : 'Painting only', note: hasFrame ? 'Confirm frame with Leslie' : 'Artwork image only' }
    ];
  }

  return [
    { label: 'Actual size', value: `${dimensions.label} in`, note: 'Listed canvas size' },
    { label: 'Scale', value: `${Math.round((dimensions.widthInches / roomPreviewReferenceWidthInches) * 100)}% of 36 in guide`, note: 'Slider adjusts camera distance' },
    { label: 'View', value: hasFrame ? 'Framed view' : 'Painting only', note: hasFrame ? 'Marketplace frame view' : 'Clean artwork view' }
  ];
}

function normalize(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

function currentSearchParams() {
  if (typeof window === 'undefined') return new URLSearchParams();
  return new URLSearchParams(window.location.search);
}

function numberFromParam(params: URLSearchParams, key: string, fallback: number, min: number, max: number) {
  if (!params.has(key)) return fallback;
  const parsed = Number(params.get(key));
  return Number.isFinite(parsed) ? clamp(parsed, min, max) : fallback;
}

function wallGuideFromParam(params: URLSearchParams) {
  const value = params.get('wall');
  if (!value) return undefined;
  if (value === 'off') return null;
  const parsed = Number(value);
  return roomWallGuideOptions.includes(parsed as (typeof roomWallGuideOptions)[number]) ? parsed : undefined;
}

function roomPlacementFromParams(params = currentSearchParams()) {
  return {
    rotation: numberFromParam(params, 'angle', 0, -12, 12),
    scale: numberFromParam(params, 'scale', 100, 65, 135),
    wallGuideInches: wallGuideFromParam(params),
    x: numberFromParam(params, 'x', 50, 12, 88),
    y: numberFromParam(params, 'y', 46, 16, 84)
  };
}

function roomPlacementParams(placement: RoomPlacementState) {
  const params: { angle: string; scale: string; wall?: string; x: string; y: string } = {
    angle: String(Math.round(placement.rotation)),
    scale: String(Math.round(placement.scale)),
    x: String(Math.round(placement.x)),
    y: String(Math.round(placement.y))
  };
  if (placement.wallGuideInches === null) {
    params.wall = 'off';
  } else if (typeof placement.wallGuideInches === 'number') {
    params.wall = String(placement.wallGuideInches);
  }
  return params;
}

function galleryStateFromUrl() {
  const params = currentSearchParams();
  const availabilityParam = params.get('availability') ?? '';
  const filterParam = params.get('filter') ?? '';
  const activeAvailabilityId =
    availabilityFilters.find((filter) => filter.id === normalize(availabilityParam))?.id ?? defaultGalleryState.activeAvailabilityId;
  const activeFilter = filters.find((filter) => normalize(filter) === normalize(filterParam)) ?? defaultGalleryState.activeFilter;

  return {
    activeAvailabilityId,
    activeFilter,
    query: params.get('q') ?? defaultGalleryState.query
  };
}

function galleryShareUrl(activeFilter: string, activeAvailabilityId: AvailabilityFilterId, query: string) {
  if (typeof window === 'undefined') return '';

  const url = new URL(window.location.href);
  const params = url.searchParams;
  const trimmedQuery = query.trim();

  params.delete('room');
  params.delete('palette');
  params.delete('saved');

  if (activeFilter === defaultGalleryState.activeFilter) {
    params.delete('filter');
  } else {
    params.set('filter', activeFilter);
  }

  if (activeAvailabilityId === defaultGalleryState.activeAvailabilityId) {
    params.delete('availability');
  } else {
    params.set('availability', activeAvailabilityId);
  }

  if (trimmedQuery) {
    params.set('q', trimmedQuery);
  } else {
    params.delete('q');
  }

  return url.toString();
}

function artworkPath(listing: MarketplaceListing) {
  return `/artwork/${slugify(listing.title)}-${listing.id}/`;
}

function listingMatchesFilter(listing: ListingView, filter: string) {
  if (filter === 'All') return true;
  return listing.tags.some((tag) => normalize(tag).includes(normalize(filter))) || normalize(listing.title).includes(normalize(filter));
}

function listingMatchesAvailability(listing: ListingView, availabilityId: AvailabilityFilterId) {
  if (availabilityId === 'all') return true;
  if (availabilityId === 'available') return !isUnavailableListing(listing);
  return listing.availability === availabilityId;
}

function listingSignal(listing: ListingView) {
  return normalize(`${listing.title} ${listing.tags.join(' ')} ${listing.imageSlug}`);
}

function curatorNote(listing: ListingView) {
  const text = listingSignal(listing);
  if (text.includes('spoonbill') || text.includes('wildlife')) {
    return 'A coastal wildlife moment that adds life and color while staying quiet enough for a refined Lowcountry room.';
  }
  if (text.includes('dock') || text.includes('beach') || text.includes('footsteps') || text.includes('harbor')) {
    return 'A gentle invitation into the scene, with a path, dock, or shoreline detail that gives the eye somewhere peaceful to land.';
  }
  if (text.includes('golden') || text.includes('sunset') || text.includes('sundown') || text.includes('glow') || text.includes('horizon')) {
    return 'Warm golden-hour light carries the composition, balanced by darker water or marsh notes so the piece feels luminous rather than loud.';
  }
  if (text.includes('reflection') || text.includes('river') || text.includes('passing') || text.includes('blue') || text.includes('tide')) {
    return 'Reflective water creates a calm visual path through the painting, making it especially useful for rooms that need depth and stillness.';
  }
  if (text.includes('woods') || text.includes('oak') || text.includes('tree')) {
    return 'A grounded Lowcountry landscape with deeper greens and natural structure, suited for spaces that need a quieter earthy anchor.';
  }
  return 'A quiet Lowcountry marsh study: low horizon, open sky, tidal calm, and the breathing room I look for in this work.';
}

function placementCues(listing: ListingView) {
  const text = listingSignal(listing);
  const dimensions = listingDimensions(listing);
  const cues = new Set<string>();
  if (listing.price <= 250) cues.add('Giftable original');
  if (dimensions?.label === '36 x 48' || listing.tags.some((tag) => ['XL', 'Statement piece'].includes(tag))) cues.add('Statement wall');
  if (dimensions?.label === '24 x 36' || listing.tags.includes('Large work')) cues.add('Larger room');
  if (framedPreview(listing)) cues.add('Frame view');
  if (['quiet', 'soft', 'tranquil', 'reflection', 'passing', 'sky'].some((term) => text.includes(term))) cues.add('Quiet room');
  if (['coastal', 'beach', 'harbor', 'dock', 'water'].some((term) => text.includes(term))) cues.add('Coastal home');
  if (['marsh', 'lowcountry', 'tide', 'sundown'].some((term) => text.includes(term))) cues.add('Lowcountry collector');
  return [...cues].slice(0, 3);
}

function galleryWallLabel(listing: ListingView) {
  const text = listingSignal(listing);
  const light = ['golden', 'sunset', 'sundown', 'glow', 'horizon', 'evening'].some((term) => text.includes(term))
    ? 'Honey-gold light'
    : ['rose', 'violet', 'soft', 'pastel', 'spoonbill', 'sky'].some((term) => text.includes(term))
      ? 'Rose and soft sky'
      : ['blue', 'water', 'river', 'reflection', 'passing', 'tide', 'harbor'].some((term) => text.includes(term))
        ? 'Tidal blue reflection'
        : ['woods', 'oak', 'green', 'marsh', 'grass'].some((term) => text.includes(term))
          ? 'Marsh green calm'
          : 'Quiet Lowcountry light';
  const room = placementCues(listing)[0] ?? 'Lowcountry collector';
  const collectingCue = framedPreview(listing)
    ? 'Real frame view'
    : hasKnownDimensions(listing)
      ? `${dimensionLabel(listing)} original`
      : 'Size coming soon';

  return { light, room, collectingCue };
}

function isSoldListing(listing: Pick<MarketplaceListing, 'availability'>) {
  return listing.availability === 'sold';
}

function isPendingListing(listing: Pick<MarketplaceListing, 'availability'>) {
  return listing.availability === 'pending';
}

function isReservedListing(listing: Pick<MarketplaceListing, 'availability'>) {
  return listing.availability === 'reserved';
}

function isHiddenListing(listing: Pick<MarketplaceListing, 'availability'>) {
  return listing.availability === 'hidden';
}

function isUnavailableListing(listing: Pick<MarketplaceListing, 'availability'>) {
  return isSoldListing(listing) || isPendingListing(listing) || isReservedListing(listing) || isHiddenListing(listing);
}

function availabilityClass(listing: Pick<MarketplaceListing, 'availability'>) {
  if (isSoldListing(listing)) return 'sold';
  if (isPendingListing(listing)) return 'pending';
  if (isReservedListing(listing)) return 'reserved';
  if (isHiddenListing(listing)) return 'hidden';
  return '';
}

function availabilityText(listing: ListingView) {
  if (isSoldListing(listing)) return 'Sold original';
  if (isPendingListing(listing)) return 'Pending original';
  if (isReservedListing(listing)) return 'Reserved original';
  if (isHiddenListing(listing)) return 'Not listed original';
  return 'Available original';
}

function priceOrSold(listing: ListingView) {
  if (isSoldListing(listing)) return 'Sold';
  if (isPendingListing(listing)) return 'Pending';
  if (isReservedListing(listing)) return 'Reserved';
  if (isHiddenListing(listing)) return 'Not listed';
  return formatPrice(listing.price);
}

function dedupeGalleryImages(listings: ListingView[]) {
  const byImage = new Map<string, ListingView>();

  for (const listing of listings) {
    const current = byImage.get(listing.image);
    if (!current || (!hasKnownDimensions(current) && hasKnownDimensions(listing))) {
      byImage.set(listing.image, listing);
    }
  }

  return listings.filter((listing) => byImage.get(listing.image)?.id === listing.id);
}

function priceLine(listing: ListingView) {
  if (isSoldListing(listing)) return `Sold - last listed ${formatPrice(listing.price)}`;
  if (isPendingListing(listing)) return `Pending - listed ${formatPrice(listing.price)}`;
  if (isReservedListing(listing)) return `Reserved - listed ${formatPrice(listing.price)}`;
  if (isHiddenListing(listing)) return 'Not listed';
  return formatPrice(listing.price);
}

function soldPrompt(listing: ListingView) {
  return listing.soldNote ?? 'This original has sold.';
}

function pendingPrompt() {
  return 'This original is pending right now.';
}

function reservedPrompt() {
  return 'This original is being held right now.';
}

function unavailablePrompt(listing: ListingView) {
  if (isSoldListing(listing)) return 'This original is no longer available.';
  if (isPendingListing(listing)) return pendingPrompt();
  if (isReservedListing(listing)) return reservedPrompt();
  return '';
}

function roomViewInquiry(listing: ListingView, mediaMode: RoomViewState['mediaMode'], placement?: RoomPlacementState) {
  const size = buyerDimensionSuffix(listing);
  const viewLine = roomViewUsesFramedImage(listing, mediaMode)
    ? 'I tried the framed view and would love to know whether that frame is included.'
    : 'I tried the painting-only view.';
  return `Hi Leslie, I tried ${listing.title} (${priceLine(listing)}${size}) in my room on Leslie's Paintings. ${viewLine} I think it may work for my wall. Could you confirm availability, pickup, delivery, or insured shipping options?\n\nRoom preview link: ${roomPreviewUrl(listing, mediaMode, placement)}`;
}

function roomViewSummary(listing: ListingView, mediaMode: RoomViewState['mediaMode']) {
  const framed = roomViewUsesFramedImage(listing, mediaMode);
  return {
    label: framed ? 'Framed Marketplace view' : 'Painting-only view',
    note: framed
      ? 'This uses the real framed listing photo. Leslie can confirm whether the frame is included.'
      : 'This uses the full artwork image. Leslie can confirm size, availability, and the best way to get it to you.'
  };
}

function roomViewMessagePreview(listing: ListingView, mediaMode: RoomViewState['mediaMode']) {
  const view = roomViewUsesFramedImage(listing, mediaMode) ? 'framed view' : 'painting-only view';
  return `Your note will include ${listing.title}, ${priceLine(listing)}, ${buyerDimensionPhrase(listing)}, and that you tried the ${view} in your room.`;
}

function roomViewVisitorSteps(listing: ListingView, mediaMode: RoomViewState['mediaMode']) {
  const view = roomViewUsesFramedImage(listing, mediaMode) ? 'framed view' : 'painting-only view';
  return [
    {
      title: 'Place',
      note: hasKnownDimensions(listing) ? `Using ${dimensionLabel(listing)} for the scale guide.` : 'Shape preview only until Leslie confirms the exact size.'
    },
    {
      title: 'Save',
      note: 'Keep a room preview image for a spouse, designer, or second look later.'
    },
    {
      title: 'Ask',
      note: `The message will say you tried the ${view} in your room.`
    }
  ];
}

function roomViewActionSummary(listing: ListingView, mediaMode: RoomViewState['mediaMode']) {
  const view = roomViewUsesFramedImage(listing, mediaMode) ? 'real framed listing photo' : 'painting-only image';
  const size = hasKnownDimensions(listing) ? `scaled from the listed ${dimensionLabel(listing)}` : 'shown as a shape preview until Leslie confirms the exact size';
  return `This uses the ${view}, ${size}. Save it, share it, or send it to Leslie with the painting details included.`;
}

function roomScaleTruthSummary(listing: ListingView, mediaMode: RoomViewState['mediaMode'], wallGuideInches: number | null) {
  const dimensions = listingDimensions(listing);
  const view = roomViewUsesFramedImage(listing, mediaMode) ? 'framed photo' : 'painting image';
  if (!dimensions) {
    return `Size coming soon. This ${view} preview is for mood, color, and shape until Leslie confirms the exact canvas size.`;
  }

  const wall = wallGuideInches ? ` against the ${wallGuideInches} in wall guide` : '';
  return `Scale uses the listed ${dimensions.label} in size${wall}. Camera distance and wall angle can still change how it feels in person.`;
}

function roomWallGuideSummary(listing: ListingView, wallGuideInches: number | null) {
  const dimensions = listingDimensions(listing);
  if (!dimensions) return 'Size coming soon. Use the preview for mood and shape until Leslie confirms the exact canvas size.';
  if (!wallGuideInches) return `Listed size is ${dimensions.label} in. Turn on a wall guide to compare it with a sofa, console, or open wall span.`;
  const percent = Math.round((dimensions.widthInches / wallGuideInches) * 100);
  return `${dimensions.label} in is about ${percent}% as wide as a ${wallGuideInches} in span.`;
}

function roomPreviewTruthNote(cameraRequested: boolean, cameraStatus: CameraStatus, arSupport: RoomArSupport) {
  if (arSupport === 'available') {
    return 'Wall mode uses your phone\'s surface finder and the listed artwork dimensions. The camera view stays on your phone.';
  }

  if (cameraStatus === 'live') {
    return 'Your camera view stays in your browser. This simple fallback can be dragged into place but does not stay pinned as you walk around.';
  }

  if (cameraRequested) {
    return 'If camera access is blocked or unavailable, the room preview still works as a quiet wall mockup you can save or share.';
  }

  return 'Camera starts only when you press the camera button. Nothing from your room is uploaded to Leslie.';
}

function roomPreviewModeFacts(cameraRequested: boolean, cameraStatus: CameraStatus, arSupport: RoomArSupport) {
  const cameraLabel = cameraStatus === 'live' ? 'Camera on' : cameraRequested ? 'Camera blocked' : 'Camera off';
  const cameraNote = cameraStatus === 'live'
    ? 'Your room stays on this device.'
    : cameraRequested
      ? 'The wall mockup still works.'
      : 'Starts only when you choose.';

  return [
    {
      label: 'Privacy',
      value: cameraLabel,
      note: cameraNote
    },
    {
      label: 'Placement',
      value: arSupport === 'available' ? 'Phone finds wall' : 'Move by hand',
      note: arSupport === 'available'
        ? 'Uses the phone surface finder instead of a flat camera overlay.'
        : 'Today this is a private camera overlay. Drag or tap until it feels right.'
    },
    {
      label: 'Wall pinning',
      value: arSupport === 'available' ? 'Available' : 'Not supported here',
      note: arSupport === 'available'
        ? 'The phone anchors the model to a detected vertical surface at its listed size.'
        : 'Use the simple preview on this device; supported phones get the pinned wall view.'
    }
  ];
}

function roomArModelUrl(listing: ListingView, mediaMode: RoomViewState['mediaMode']) {
  if (!hasKnownDimensions(listing)) return null;
  const view = roomViewUsesFramedImage(listing, mediaMode) ? 'framed' : 'painting';
  return `/ar/${listing.id}-${view}.glb`;
}

function artworkUrl(listing: MarketplaceListing) {
  return `https://lesliespaintings.com${artworkPath(listing)}`;
}

function roomPreviewUrl(listing: ListingView, mediaMode: RoomViewState['mediaMode'] = 'artwork', placement?: RoomPlacementState) {
  const url = new URL('https://lesliespaintings.com/');
  url.searchParams.set('preview', listing.id);
  if (roomViewUsesFramedImage(listing, mediaMode)) {
    url.searchParams.set('view', 'framed');
  }
  if (placement) {
    const params = roomPlacementParams(placement);
    url.searchParams.set('x', params.x);
    url.searchParams.set('y', params.y);
    url.searchParams.set('scale', params.scale);
    url.searchParams.set('angle', params.angle);
    if (params.wall) url.searchParams.set('wall', params.wall);
  }
  return url.toString();
}

function roomPreviewFileName(listing: ListingView, mediaMode: RoomViewState['mediaMode']) {
  const view = roomViewUsesFramedImage(listing, mediaMode) ? 'framed-room-preview' : 'painting-room-preview';
  return `${slugify(listing.title)}-${view}-leslie-roberts.png`;
}

function frameStatus(listing: ListingView) {
  return framedPreview(listing)
    ? 'Marketplace frame media is shown for this listing; confirm whether the shown frame is included before purchase.'
    : 'No Marketplace frame view is attached to this listing; ask me whether framing is available or sold separately.';
}

function artAlt(listing: ListingView) {
  const size = hasKnownDimensions(listing) ? `${dimensionLabel(listing)} ` : '';
  if (isSoldListing(listing)) {
    return `Sold original ${size}Lowcountry painting "${listing.title}" by Leslie Roberts.`;
  }
  if (isPendingListing(listing)) {
    return `Pending original ${size}Lowcountry painting "${listing.title}" by Leslie Roberts.`;
  }
  if (isReservedListing(listing)) {
    return `Reserved original ${size}Lowcountry painting "${listing.title}" by Leslie Roberts.`;
  }
  return `Original ${size}Lowcountry painting "${listing.title}" by Leslie Roberts, available in ${listing.location} for ${formatPrice(listing.price)}.`;
}

function mediaAlt(listing: ListingView, media: MarketplaceMedia) {
  if (media.kind === 'framed') {
    return `${media.label} for Leslie Roberts original Lowcountry painting "${listing.title}", showing the black frame from the Facebook Marketplace listing.`;
  }
  if (media.kind === 'detail') {
    return `${media.label} for Leslie Roberts original Lowcountry painting "${listing.title}", showing canvas texture and brushwork.`;
  }
  if (media.kind === 'video') {
    return `${media.label} for Leslie Roberts original Lowcountry painting "${listing.title}".`;
  }
  return `${media.label} for Leslie Roberts original Lowcountry painting "${listing.title}".`;
}

function mediaDisplayLabel(media: MarketplaceMedia) {
  const label = normalize(media.label);
  if (media.kind === 'video') return 'Video';
  if (media.kind === 'framed') return 'Framed view';
  if (media.kind === 'context') return 'In room';
  if (media.kind === 'artwork') return 'Full artwork';
  if (label.includes('signature') || label.includes('corner') || label.includes('edge') || label.includes('frame detail')) return 'Signature/detail';
  return 'Close-up';
}

function framedPreview(listing: ListingView) {
  return listing.media.find((media) => media.type === 'image' && media.kind === 'framed');
}

function framedRoomPreview(listing: ListingView) {
  const framedImages = listing.media.filter((media) => media.type === 'image' && media.kind === 'framed');
  if (framedImages.length === 0) return undefined;

  const fullFrame = framedImages.find((media) => /close|full|front/i.test(media.label));
  if (fullFrame) return fullFrame;

  const roomFrame = framedImages.find((media) => /room|wall|table|mounted|sofa/i.test(`${media.label} ${media.note ?? ''} ${media.src}`));
  return roomFrame ?? framedImages[0];
}

function roomViewUsesFramedImage(listing: ListingView, mediaMode: RoomViewState['mediaMode']) {
  return mediaMode === 'framed' && Boolean(framedRoomPreview(listing));
}

function livingAnimationSource(listing: ListingView) {
  const signal = listingSignal(listing);
  if (signal.includes('spoonbill') || signal.includes('wildlife')) {
    return '/art/living/roseate-spoonbill-living-leslies-paintings-leslie-roberts.gif';
  }
  return null;
}

function seededNumber(seed: string, salt: number) {
  let value = 2166136261 + salt;
  for (let index = 0; index < seed.length; index += 1) {
    value ^= seed.charCodeAt(index);
    value = Math.imul(value, 16777619);
  }
  return (value >>> 0) / 4294967295;
}

function livingPalette(listing: ListingView) {
  const signal = listingSignal(listing);
  if (signal.includes('spoonbill') || signal.includes('rose') || signal.includes('violet') || signal.includes('soft')) {
    return {
      sky: 'rgba(244, 173, 176, 0.25)',
      light: 'rgba(255, 226, 167, 0.2)',
      water: 'rgba(181, 215, 219, 0.22)',
      grass: 'rgba(60, 89, 57, 0.26)'
    };
  }
  if (signal.includes('blue') || signal.includes('river') || signal.includes('reflection') || signal.includes('tide') || signal.includes('water')) {
    return {
      sky: 'rgba(178, 213, 220, 0.22)',
      light: 'rgba(244, 210, 143, 0.16)',
      water: 'rgba(118, 175, 190, 0.26)',
      grass: 'rgba(45, 76, 55, 0.24)'
    };
  }
  if (signal.includes('golden') || signal.includes('sunset') || signal.includes('glow') || signal.includes('sundown') || signal.includes('horizon')) {
    return {
      sky: 'rgba(248, 201, 111, 0.3)',
      light: 'rgba(255, 224, 151, 0.26)',
      water: 'rgba(212, 155, 72, 0.18)',
      grass: 'rgba(44, 75, 43, 0.28)'
    };
  }
  return {
    sky: 'rgba(191, 216, 211, 0.18)',
    light: 'rgba(239, 204, 137, 0.16)',
    water: 'rgba(137, 178, 184, 0.18)',
    grass: 'rgba(52, 82, 51, 0.26)'
  };
}

function LivingArtworkView({ listing }: { listing: ListingView }) {
  const customAnimation = livingAnimationSource(listing);

  if (customAnimation) {
    return (
      <div className="living-canvas-wrap custom-living-gif">
        <img src={customAnimation} alt={`Living view animation for ${listing.title} by Leslie Roberts`} />
      </div>
    );
  }

  return <LivingArtworkCanvas listing={listing} />;
}

function LivingArtworkCanvas({ listing }: { listing: ListingView }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [imageReady, setImageReady] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const context = canvas.getContext('2d');
    if (!context) return undefined;

    const drawingCanvas = canvas;
    const drawingContext = context;
    let frame = 0;
    let animationFrame = 0;
    let cancelled = false;
    const image = new Image();
    image.decoding = 'async';
    image.src = listing.image;
    const palette = livingPalette(listing);
    const seed = listing.id;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function resizeCanvas() {
      const rect = drawingCanvas.getBoundingClientRect();
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      drawingCanvas.width = Math.max(1, Math.round(rect.width * ratio));
      drawingCanvas.height = Math.max(1, Math.round(rect.height * ratio));
      drawingContext.setTransform(ratio, 0, 0, ratio, 0, 0);
    }

    function containedArtworkRect(width: number, height: number) {
      const sourceWidth = image.naturalWidth || width;
      const sourceHeight = image.naturalHeight || height;
      const maxWidth = width * 0.9;
      const maxHeight = height * 0.9;
      const scale = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight);
      const drawWidth = sourceWidth * scale;
      const drawHeight = sourceHeight * scale;

      return {
        x: (width - drawWidth) / 2,
        y: (height - drawHeight) / 2,
        width: drawWidth,
        height: drawHeight
      };
    }

    function render(time = 0) {
      if (cancelled) return;
      resizeCanvas();

      const rect = drawingCanvas.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;
      const slow = reduceMotion ? 0 : time / 1000;
      const phase = slow * 0.28 + seededNumber(seed, 1) * Math.PI * 2;
      const art = containedArtworkRect(width, height);

      drawingContext.clearRect(0, 0, width, height);
      drawingContext.fillStyle = '#0e1711';
      drawingContext.fillRect(0, 0, width, height);

      drawingContext.save();
      drawingContext.shadowColor = 'rgba(0, 0, 0, 0.32)';
      drawingContext.shadowBlur = 28;
      drawingContext.shadowOffsetY = 14;
      drawingContext.drawImage(image, art.x, art.y, art.width, art.height);
      drawingContext.restore();

      drawingContext.save();
      drawingContext.beginPath();
      drawingContext.rect(art.x, art.y, art.width, art.height);
      drawingContext.clip();
      drawingContext.globalCompositeOperation = 'screen';
      const skyGradient = drawingContext.createLinearGradient(art.x, art.y, art.x + art.width, art.y + art.height * 0.72);
      skyGradient.addColorStop(0, palette.sky);
      skyGradient.addColorStop(0.42 + Math.sin(slow * 0.12) * 0.04, palette.light);
      skyGradient.addColorStop(1, 'rgba(255,255,255,0)');
      drawingContext.fillStyle = skyGradient;
      drawingContext.fillRect(art.x, art.y, art.width, art.height);

      const glowX = art.x + art.width * (0.28 + seededNumber(seed, 3) * 0.46) + Math.sin(slow * 0.19) * art.width * 0.08;
      const glowY = art.y + art.height * (0.22 + seededNumber(seed, 4) * 0.22);
      const glow = drawingContext.createRadialGradient(glowX, glowY, 0, glowX, glowY, art.width * 0.58);
      glow.addColorStop(0, palette.light);
      glow.addColorStop(0.36, 'rgba(255, 219, 145, 0.08)');
      glow.addColorStop(1, 'rgba(255,255,255,0)');
      drawingContext.fillStyle = glow;
      drawingContext.fillRect(art.x, art.y, art.width, art.height);
      drawingContext.restore();

      drawingContext.save();
      drawingContext.beginPath();
      drawingContext.rect(art.x, art.y, art.width, art.height);
      drawingContext.clip();
      drawingContext.globalCompositeOperation = 'soft-light';
      for (let index = 0; index < 9; index += 1) {
        const y = art.y + art.height * (0.18 + seededNumber(seed, 20 + index) * 0.28);
        const x =
          art.x +
          ((slow * (3 + index * 0.24) + seededNumber(seed, 40 + index) * art.width * 2) % (art.width * 1.55)) -
          art.width * 0.28;
        drawingContext.beginPath();
        drawingContext.ellipse(
          x,
          y,
          art.width * (0.08 + seededNumber(seed, 60 + index) * 0.16),
          art.height * 0.015,
          0,
          0,
          Math.PI * 2
        );
        drawingContext.fillStyle = index % 2 ? 'rgba(255, 255, 255, 0.07)' : 'rgba(244, 190, 177, 0.08)';
        drawingContext.fill();
      }
      drawingContext.restore();

      drawingContext.save();
      drawingContext.beginPath();
      drawingContext.rect(art.x, art.y, art.width, art.height);
      drawingContext.clip();
      drawingContext.globalCompositeOperation = 'screen';
      drawingContext.lineCap = 'round';
      for (let index = 0; index < 16; index += 1) {
        const baseY = art.y + art.height * (0.53 + seededNumber(seed, 80 + index) * 0.28);
        const wave = Math.sin(slow * (0.65 + seededNumber(seed, 100 + index) * 0.4) + index * 0.9) * art.height * 0.006;
        const x = art.x + art.width * seededNumber(seed, 120 + index);
        const length = art.width * (0.09 + seededNumber(seed, 140 + index) * 0.17);
        drawingContext.strokeStyle = index % 3 === 0 ? palette.water : 'rgba(255, 238, 185, 0.14)';
        drawingContext.lineWidth = 0.6 + seededNumber(seed, 160 + index) * 1.1;
        drawingContext.beginPath();
        drawingContext.moveTo(x - length / 2, baseY + wave);
        drawingContext.quadraticCurveTo(x, baseY - wave * 1.6, x + length / 2, baseY + wave * 0.7);
        drawingContext.stroke();
      }
      drawingContext.restore();

      drawingContext.save();
      drawingContext.beginPath();
      drawingContext.rect(art.x, art.y, art.width, art.height);
      drawingContext.clip();
      drawingContext.globalCompositeOperation = 'multiply';
      drawingContext.strokeStyle = palette.grass;
      drawingContext.lineWidth = 1;
      for (let index = 0; index < 36; index += 1) {
        const baseX = art.x + art.width * seededNumber(seed, 200 + index);
        const baseY = art.y + art.height * (0.66 + seededNumber(seed, 230 + index) * 0.28);
        const heightStroke = art.height * (0.025 + seededNumber(seed, 260 + index) * 0.055);
        const lean = Math.sin(slow * 0.52 + index * 0.43) * art.width * 0.006;
        drawingContext.beginPath();
        drawingContext.moveTo(baseX, baseY);
        drawingContext.quadraticCurveTo(baseX + lean, baseY - heightStroke * 0.55, baseX + lean * 1.6, baseY - heightStroke);
        drawingContext.stroke();
      }
      drawingContext.restore();

      drawingContext.save();
      drawingContext.beginPath();
      drawingContext.rect(art.x, art.y, art.width, art.height);
      drawingContext.clip();
      drawingContext.globalCompositeOperation = 'screen';
      for (let index = 0; index < 7; index += 1) {
        const twinkle = Math.max(0, Math.sin(slow * (0.42 + index * 0.08) + seededNumber(seed, 300 + index) * 8));
        if (twinkle < 0.72) continue;
        const x = art.x + art.width * (0.18 + seededNumber(seed, 320 + index) * 0.68);
        const y = art.y + art.height * (0.32 + seededNumber(seed, 340 + index) * 0.46);
        drawingContext.fillStyle = `rgba(255, 232, 171, ${(twinkle - 0.72) * 0.22})`;
        drawingContext.beginPath();
        drawingContext.arc(x, y, 1.2 + seededNumber(seed, 360 + index) * 2.8, 0, Math.PI * 2);
        drawingContext.fill();
      }
      drawingContext.restore();

      drawingContext.save();
      drawingContext.strokeStyle = 'rgba(251, 250, 246, 0.28)';
      drawingContext.lineWidth = 1;
      drawingContext.strokeRect(art.x, art.y, art.width, art.height);
      drawingContext.restore();

      frame += 1;
      if (reduceMotion && frame > 1) return;
      animationFrame = window.requestAnimationFrame(render);
    }

    image.onload = () => {
      if (cancelled) return;
      setImageReady(true);
      render();
    };
    image.onerror = () => setImageReady(false);
    window.addEventListener('resize', resizeCanvas);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [listing]);

  return (
    <div className="living-canvas-wrap">
      <canvas ref={canvasRef} aria-label={`Living view animation for ${listing.title}`} />
      {!imageReady ? <span>Preparing the light...</span> : null}
    </div>
  );
}

export function App() {
  const initialGalleryState = galleryStateFromUrl();
  const roomVideoRef = useRef<HTMLVideoElement | null>(null);
  const roomArModelRef = useRef<RoomArModelElement | null>(null);
  const roomPreviewOpenedRef = useRef(false);
  const roomDragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    baseX: number;
    baseY: number;
    width: number;
    height: number;
  } | null>(null);
  const livingClickCountsRef = useRef<Record<string, number>>({});
  const pixelBySlug = useMemo(() => new Map(typedPixels.map((work) => [work.slug, work])), []);
  const listings = useMemo<ListingView[]>(
    () =>
      marketplaceListings
        .filter((listing) => !isHiddenListing(listing))
        .map((listing) => {
          const work = pixelBySlug.get(listing.imageSlug);
          const currentArtworkPhoto = listing.media?.find((media) => media.type === 'image' && media.kind === 'artwork')?.src;
          const primaryImage = listing.marketplaceImage ?? currentArtworkPhoto ?? work?.imagePath ?? '/art/facebook/saltgrass-glow-original-lowcountry-marsh-painting-facebook-marketplace.jpg';
          return {
            ...listing,
            image: primaryImage,
            pixelsUrl: work?.pixelsUrl,
            printPrice: work?.printPrice,
            matchedTitle: work?.title,
            media:
              listing.media ??
              [
                {
                  type: 'image',
                  src: primaryImage,
                  label: 'Artwork view',
                  kind: 'artwork'
                }
              ]
          };
        }),
    [pixelBySlug]
  );

  const [activeFilter, setActiveFilter] = useState(initialGalleryState.activeFilter);
  const [activeAvailabilityId, setActiveAvailabilityId] = useState<AvailabilityFilterId>(initialGalleryState.activeAvailabilityId);
  const [query, setQuery] = useState(initialGalleryState.query);
  const [selected, setSelected] = useState<ListingView | null>(null);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [quickFrameId, setQuickFrameId] = useState<string | null>(null);
  const [roomPreviewStatus, setRoomPreviewStatus] = useState<'idle' | 'preparing' | 'shared' | 'saved' | 'copied' | 'error' | 'view'>('idle');
  const [roomView, setRoomView] = useState<RoomViewState | null>(null);
  const [roomArSupport, setRoomArSupport] = useState<RoomArSupport>('checking');
  const [roomArSessionStatus, setRoomArSessionStatus] = useState<RoomArSessionStatus>('idle');
  const [livingView, setLivingView] = useState<LivingViewState | null>(null);
  const [fullscreenView, setFullscreenView] = useState<FullscreenArtViewState | null>(null);
  const [cameraStatus, setCameraStatus] = useState<CameraStatus>('idle');
  const [cameraRequestCount, setCameraRequestCount] = useState(0);
  const [cameraError, setCameraError] = useState('');
  const [isWallPreviewDevice, setIsWallPreviewDevice] = useState(false);
  const [contactForm, setContactForm] = useState<ContactFormState>(() => createContactFormState());
  const [contactStatus, setContactStatus] = useState<ContactFormStatus>('idle');
  const [contactFeedback, setContactFeedback] = useState('');
  const publicListings = listings.filter((listing) => !isHiddenListing(listing));
  const orderedListings = [...publicListings].sort((a, b) => Number(isUnavailableListing(a)) - Number(isUnavailableListing(b)));
  const availableListings = publicListings.filter((listing) => !isUnavailableListing(listing));
  const listingsMatchingCurrentMood = orderedListings.filter((listing) => {
    const text = normalize(`${listing.title} ${listing.tags.join(' ')} ${listing.location} ${curatorNote(listing)} ${placementCues(listing).join(' ')}`);
    return listingMatchesFilter(listing, activeFilter) && text.includes(normalize(query));
  });
  const availabilityFilterCounts = availabilityFilters.map((filter) => ({
    ...filter,
    count: listingsMatchingCurrentMood.filter((listing) => listingMatchesAvailability(listing, filter.id)).length
  }));
  const primaryAvailabilityFilters = availabilityFilterCounts.filter((filter) => filter.primary || filter.id === activeAvailabilityId);
  const secondaryAvailabilityFilters = availabilityFilterCounts.filter((filter) => !filter.primary && filter.id !== activeAvailabilityId);
  const filteredListings = orderedListings.filter((listing) => {
    const text = normalize(`${listing.title} ${listing.tags.join(' ')} ${listing.location} ${curatorNote(listing)} ${placementCues(listing).join(' ')}`);
    return (
      listingMatchesFilter(listing, activeFilter) &&
      listingMatchesAvailability(listing, activeAvailabilityId) &&
      text.includes(normalize(query))
    );
  });
  const visibleListings = dedupeGalleryImages(filteredListings);
  const contactArtwork = availableListings.find((listing) => listing.id === contactForm.artworkId);

  async function copyRawText(value: string) {
    try {
      if (!navigator.clipboard?.writeText || !window.isSecureContext) {
        throw new Error('Clipboard API unavailable');
      }
      await navigator.clipboard.writeText(value);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  }

  function updateContactField(field: keyof ContactFormState, value: string) {
    setContactForm((current) => ({
      ...current,
      [field]: value
    }));
    if (contactStatus !== 'idle') {
      setContactStatus('idle');
      setContactFeedback('');
    }
  }

  function resetTurnstile() {
    const browserWindow = window as typeof window & { turnstile?: { reset: () => void } };
    browserWindow.turnstile?.reset();
  }

  async function submitContactForm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setContactStatus('sending');
    setContactFeedback('');

    const formData = new FormData(event.currentTarget);
    const artwork = contactArtwork;
    // The Worker re-validates every field before sending email; the client only
    // adds the selected artwork context and current page for a friendlier note.
    const payload = {
      ...contactForm,
      artworkTitle: artwork ? `${artwork.title} - ${formatPrice(artwork.price)}` : 'Not sure yet',
      artworkUrl: artwork ? artworkUrl(artwork) : '',
      cfTurnstileResponse: String(formData.get('cf-turnstile-response') ?? ''),
      pageUrl: window.location.href
    };

    try {
      const response = await fetch('/api/contact', {
        body: JSON.stringify(payload),
        headers: {
          'content-type': 'application/json'
        },
        method: 'POST'
      });
      const result = (await response.json().catch(() => null)) as { message?: string } | null;
      if (!response.ok) {
        throw new Error(result?.message ?? 'The note did not send. Please try again.');
      }

      setContactStatus('sent');
      setContactFeedback(result?.message ?? 'Thank you. Your note has been sent.');
      setContactForm(createContactFormState());
      resetTurnstile();
    } catch (error) {
      setContactStatus('error');
      setContactFeedback(error instanceof Error ? error.message : 'The note did not send. Please try again.');
      resetTurnstile();
    }
  }

  function openDetails(listing: ListingView, mediaIndex = 0) {
    setLivingView(null);
    setSelected(listing);
    setSelectedMediaIndex(mediaIndex);
    setQuickFrameId(null);
  }

  function openFullscreenArt(listing: ListingView, media: MarketplaceMedia) {
    if (media.kind === 'video') return;
    setFullscreenView({ listing, media });
  }

  function closeFullscreenArt() {
    setFullscreenView(null);
  }

  function registerLivingTap(listing: ListingView) {
    const nextCount = (livingClickCountsRef.current[listing.id] ?? 0) + 1;
    livingClickCountsRef.current[listing.id] = nextCount;

    if (nextCount >= 8) {
      setLivingView({ listing, unlockedAt: Date.now() });
      setSelected(null);
      setQuickFrameId(null);
      return true;
    }

    return false;
  }

  function closeLivingView() {
    setLivingView(null);
  }

  function openRoomView(listing: ListingView, mediaMode: RoomViewState['mediaMode'] = 'artwork', placement: RoomPlacementState = roomPlacementFromParams(new URLSearchParams())) {
    const activeMode = roomViewUsesFramedImage(listing, mediaMode) ? 'framed' : 'artwork';
    setCameraError('');
    setCameraStatus('idle');
    setRoomPreviewStatus('idle');
    setRoomArSupport('checking');
    setRoomArSessionStatus('idle');
    setRoomView({
      cameraRequested: false,
      listing,
      mediaMode: activeMode,
      wallGuideInches: hasKnownDimensions(listing) ? placement.wallGuideInches ?? 72 : null,
      x: placement.x,
      y: placement.y,
      scale: placement.scale,
      rotation: placement.rotation
    });
    syncRoomPreviewUrl(listing, activeMode, placement);
  }

  function detailRoomViewMode(media: MarketplaceMedia | undefined): RoomViewState['mediaMode'] {
    return media?.kind === 'framed' ? 'framed' : 'artwork';
  }

  function closeRoomView() {
    setRoomView(null);
    setCameraError('');
    setCameraStatus('idle');
    setRoomArSessionStatus('idle');
    roomDragRef.current = null;
    clearRoomPreviewUrl();
  }

  function updateRoomView(updates: Partial<Omit<RoomViewState, 'listing'>>) {
    setRoomView((current) =>
      current
        ? {
            ...current,
            ...updates
          }
        : current
    );
  }

  function updateRoomPlacement(updates: Partial<RoomPlacementState>) {
    if (!roomView) return;
    const nextPlacement: RoomPlacementState = {
      rotation: updates.rotation ?? roomView.rotation,
      scale: updates.scale ?? roomView.scale,
      wallGuideInches: updates.wallGuideInches !== undefined ? updates.wallGuideInches : roomView.wallGuideInches,
      x: updates.x ?? roomView.x,
      y: updates.y ?? roomView.y
    };
    updateRoomView(nextPlacement);
    syncRoomPreviewUrl(roomView.listing, roomView.mediaMode, nextPlacement);
  }

  function syncRoomPreviewUrl(listing: ListingView, mediaMode: RoomViewState['mediaMode'], placement?: RoomPlacementState) {
    const url = new URL(window.location.href);
    url.searchParams.set('preview', listing.id);
    if (roomViewUsesFramedImage(listing, mediaMode)) {
      url.searchParams.set('view', 'framed');
    } else {
      url.searchParams.delete('view');
    }
    if (placement) {
      const params = roomPlacementParams(placement);
      url.searchParams.set('x', params.x);
      url.searchParams.set('y', params.y);
      url.searchParams.set('scale', params.scale);
      url.searchParams.set('angle', params.angle);
      if (params.wall) {
        url.searchParams.set('wall', params.wall);
      } else {
        url.searchParams.delete('wall');
      }
    }
    window.history.replaceState({}, '', url.toString());
  }

  function clearRoomPreviewUrl() {
    const url = new URL(window.location.href);
    url.searchParams.delete('preview');
    url.searchParams.delete('view');
    url.searchParams.delete('x');
    url.searchParams.delete('y');
    url.searchParams.delete('scale');
    url.searchParams.delete('angle');
    url.searchParams.delete('wall');
    window.history.replaceState({}, '', url.toString());
  }

  function setRoomViewMode(mediaMode: RoomViewState['mediaMode']) {
    if (!roomView) return;
    const activeMode = roomViewUsesFramedImage(roomView.listing, mediaMode) ? 'framed' : 'artwork';
    updateRoomView({ mediaMode: activeMode });
    syncRoomPreviewUrl(roomView.listing, activeMode, roomView);
    setRoomPreviewStatus('view');
    window.setTimeout(() => setRoomPreviewStatus('idle'), 1800);
  }

  function resetRoomViewPlacement() {
    updateRoomPlacement({
      x: 50,
      y: 46,
      scale: 100,
      rotation: 0,
      wallGuideInches: roomView && hasKnownDimensions(roomView.listing) ? 72 : null
    });
  }

  function applyRoomPlacementPreset(preset: (typeof roomPlacementPresets)[number]) {
    updateRoomPlacement({
      x: preset.x,
      y: preset.y,
      scale: preset.scale,
      rotation: 0
    });
  }

  function requestRoomCamera() {
    setCameraError('');
    setCameraStatus('idle');
    setCameraRequestCount((count) => count + 1);
    updateRoomView({ cameraRequested: true });
  }

  function startNativeWallPlacement() {
    if (!roomView || !hasKnownDimensions(roomView.listing)) {
      setRoomArSessionStatus('failed');
      return;
    }

    const model = roomArModelRef.current;
    if (!model?.activateAR || !model.canActivateAR) {
      setRoomArSupport('unavailable');
      setRoomArSessionStatus('failed');
      return;
    }

    setRoomArSessionStatus('starting');
    // activateAR must run directly from this click for mobile browsers to allow it.
    void model.activateAR().catch(() => {
      setRoomArSessionStatus('failed');
    });
  }

  function placeRoomArtwork(event: ReactPointerEvent<HTMLDivElement>) {
    if (!roomView) return;
    const target = event.target;
    if (target instanceof Element && target.closest('.room-artwork-frame, .room-camera-button')) return;
    const rect = event.currentTarget.getBoundingClientRect();
    updateRoomPlacement({
      x: clamp(((event.clientX - rect.left) / rect.width) * 100, 12, 88),
      y: clamp(((event.clientY - rect.top) / rect.height) * 100, 16, 84)
    });
  }

  function messageLeslieFromRoomView() {
    if (!roomView) return;
    const listing = roomView.listing;
    setContactForm((current) => ({
      ...current,
      artworkId: listing.id,
      intent: 'Room fit',
      message: roomViewInquiry(listing, roomView.mediaMode, roomView),
      startedAt: Date.now()
    }));
    setContactStatus('idle');
    setContactFeedback('');
    closeRoomView();
    window.setTimeout(() => {
      document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const messageField = document.querySelector<HTMLTextAreaElement>('textarea[name="message"]');
      messageField?.focus({ preventScroll: true });
    }, 60);
  }

  async function copyRoomPreviewMessage() {
    if (!roomView) return;

    try {
      await copyRawText(roomViewInquiry(roomView.listing, roomView.mediaMode, roomView));
      setRoomPreviewStatus('copied');
      window.setTimeout(() => setRoomPreviewStatus('idle'), 2400);
    } catch {
      setRoomPreviewStatus('error');
      window.setTimeout(() => setRoomPreviewStatus('idle'), 3200);
    }
  }

  async function shareRoomPreview() {
    if (!roomView) return;
    setRoomPreviewStatus('preparing');
    const listing = roomView.listing;
    const url = roomPreviewUrl(listing, roomView.mediaMode, roomView);
    const view = roomViewUsesFramedImage(listing, roomView.mediaMode) ? 'framed view' : 'painting-only view';
    const text = `I tried ${listing.title} (${priceLine(listing)}, ${buyerDimensionPhrase(listing)}) in my room using the ${view} preview on Leslie's Paintings. Could this work on this wall?`;
    const shareNavigator = navigator as Navigator & { canShare?: (data: ShareData) => boolean };

    try {
      if (navigator.share && roomDisplayImage && shareNavigator.canShare) {
        const blob = await createRoomPreviewBlob();
        if (blob) {
          const file = new File([blob], roomPreviewFileName(listing, roomView.mediaMode), { type: blob.type || 'image/png' });
          if (shareNavigator.canShare?.({ files: [file] })) {
            await navigator.share({
              title: `${listing.title} by Leslie Roberts`,
              text,
              url,
              files: [file]
            });
            setRoomPreviewStatus('shared');
            window.setTimeout(() => setRoomPreviewStatus('idle'), 2400);
            return;
          }
        }
      }

      if (navigator.share) {
        await navigator.share({
          title: `${listing.title} by Leslie Roberts`,
          text,
          url
        });
      } else {
        await copyRawText(`${text}\n${url}`);
      }
      setRoomPreviewStatus('shared');
      window.setTimeout(() => setRoomPreviewStatus('idle'), 2400);
    } catch (error) {
      // The native share sheet can be dismissed; keep the room view open without treating that as a broken preview.
      if (error instanceof DOMException && error.name === 'AbortError') {
        setRoomPreviewStatus('idle');
        return;
      }
      setRoomPreviewStatus('error');
      window.setTimeout(() => setRoomPreviewStatus('idle'), 3200);
    }
  }

  async function createRoomPreviewBlob() {
    if (!roomView || !roomDisplayImage) return null;

    const listing = roomView.listing;
    const canvas = document.createElement('canvas');
    canvas.width = 1200;
    canvas.height = 1600;
    const context = canvas.getContext('2d');
    if (!context) return null;
    const ctx = context;

    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.decoding = 'async';

    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Image could not be loaded for preview download.'));
      image.src = roomDisplayImage;
    });

    function drawImageContained(targetX: number, targetY: number, targetWidth: number, targetHeight: number) {
      const naturalRatio = image.naturalWidth && image.naturalHeight ? image.naturalWidth / image.naturalHeight : targetWidth / targetHeight;
      const targetRatio = targetWidth / targetHeight;
      const drawWidth = naturalRatio > targetRatio ? targetWidth : targetHeight * naturalRatio;
      const drawHeight = naturalRatio > targetRatio ? targetWidth / naturalRatio : targetHeight;
      ctx.drawImage(image, targetX + (targetWidth - drawWidth) / 2, targetY + (targetHeight - drawHeight) / 2, drawWidth, drawHeight);
    }

    function fitRoomPreviewText(text: string, fontWeight: number, fontFamily: string, startSize: number, minSize: number, maxWidth: number) {
      let size = startSize;
      do {
        ctx.font = `${fontWeight} ${size}px ${fontFamily}`;
        if (ctx.measureText(text).width <= maxWidth || size <= minSize) return size;
        size -= 2;
      } while (size >= minSize);
      return minSize;
    }

    function drawRoomPreviewLine(text: string, x: number, y: number, maxWidth: number, fontWeight: number, fontFamily: string, startSize: number, minSize: number) {
      const size = fitRoomPreviewText(text, fontWeight, fontFamily, startSize, minSize, maxWidth);
      ctx.font = `${fontWeight} ${size}px ${fontFamily}`;
      ctx.fillText(text, x, y);
    }

    const video = roomVideoRef.current;
    const canUseLiveWall = cameraStatus === 'live' && video && video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0;

    if (canUseLiveWall) {
      const videoRatio = video.videoWidth / video.videoHeight;
      const canvasRatio = canvas.width / canvas.height;
      const drawHeight = videoRatio > canvasRatio ? canvas.height : canvas.width / videoRatio;
      const drawWidth = videoRatio > canvasRatio ? canvas.height * videoRatio : canvas.width;
      context.drawImage(video, (canvas.width - drawWidth) / 2, (canvas.height - drawHeight) / 2, drawWidth, drawHeight);
      context.fillStyle = 'rgba(255, 253, 248, 0.06)';
      context.fillRect(0, 0, canvas.width, canvas.height);
    } else {
      const wallGradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
      wallGradient.addColorStop(0, '#fffaf0');
      wallGradient.addColorStop(0.52, '#f4efe4');
      wallGradient.addColorStop(1, '#dfe7df');
      context.fillStyle = wallGradient;
      context.fillRect(0, 0, canvas.width, canvas.height);

      context.fillStyle = 'rgba(156, 118, 39, 0.08)';
      context.fillRect(0, 1050, canvas.width, 550);
      context.fillStyle = 'rgba(35, 75, 54, 0.08)';
      context.fillRect(0, 1085, canvas.width, 8);
    }

    const dimensions = listingDimensions(listing);
    const ratio = dimensions ? dimensions.widthInches / dimensions.heightInches : 4 / 3;
    const artworkWidth = clamp((roomView.scale / 100) * 360 * (dimensions ? dimensions.widthInches / 24 : 1), 230, 640);
    const artworkHeight = artworkWidth / ratio;
    const centerX = (roomView.x / 100) * canvas.width;
    const centerY = canUseLiveWall ? (roomView.y / 100) * canvas.height : 300 + (roomView.y / 100) * 760;
    const isFramedPhoto = roomViewUsesFramedImage(listing, roomView.mediaMode);
    const framePad = isFramedPhoto ? 0 : 18;
    const wallAnchorY = centerY + artworkHeight / 2 + framePad + 36;
    const wallGuideInches = dimensions && roomView.wallGuideInches ? roomView.wallGuideInches : null;

    if (dimensions && wallGuideInches) {
      const guideWidth = clamp(artworkWidth * (wallGuideInches / dimensions.widthInches), 390, canvas.width - 170);
      const guideY = Math.max(120, centerY - artworkHeight / 2 - framePad - 58);
      const guideX = clamp(centerX - guideWidth / 2, 80, canvas.width - guideWidth - 80);

      context.save();
      context.strokeStyle = canUseLiveWall ? 'rgba(255, 253, 248, 0.78)' : 'rgba(156, 118, 39, 0.56)';
      context.lineWidth = 3;
      context.setLineDash([18, 14]);
      context.beginPath();
      context.moveTo(guideX, guideY);
      context.lineTo(guideX + guideWidth, guideY);
      context.stroke();
      context.setLineDash([]);
      context.beginPath();
      context.moveTo(guideX, guideY - 15);
      context.lineTo(guideX, guideY + 15);
      context.moveTo(guideX + guideWidth, guideY - 15);
      context.lineTo(guideX + guideWidth, guideY + 15);
      context.stroke();
      context.fillStyle = canUseLiveWall ? 'rgba(17, 25, 20, 0.72)' : 'rgba(255, 253, 248, 0.86)';
      context.strokeStyle = canUseLiveWall ? 'rgba(255, 253, 248, 0.2)' : 'rgba(156, 118, 39, 0.2)';
      context.beginPath();
      context.roundRect(centerX - 122, guideY - 46, 244, 33, 16);
      context.fill();
      context.stroke();
      context.fillStyle = canUseLiveWall ? '#fffdfa' : '#7a5c22';
      context.font = '800 20px system-ui, sans-serif';
      context.textAlign = 'center';
      context.fillText(`${wallGuideInches} in wall guide`, centerX, guideY - 23);
      context.textAlign = 'start';
      context.restore();
    }

    context.save();
    context.globalAlpha = canUseLiveWall ? 0.14 : 0.22;
    context.fillStyle = 'rgba(0, 0, 0, 0.28)';
    context.beginPath();
    context.ellipse(centerX + 18, wallAnchorY, artworkWidth * 0.5, Math.max(14, artworkHeight * 0.04), 0, 0, Math.PI * 2);
    context.fill();
    context.restore();

    context.save();
    context.translate(centerX, centerY);
    context.rotate((roomView.rotation * Math.PI) / 180);
    context.shadowColor = 'rgba(0, 0, 0, 0.26)';
    context.shadowBlur = isFramedPhoto ? 44 : 32;
    context.shadowOffsetX = isFramedPhoto ? 22 : 15;
    context.shadowOffsetY = isFramedPhoto ? 28 : 20;
    context.fillStyle = isFramedPhoto ? '#121712' : '#151a15';
    context.fillRect(-artworkWidth / 2 - framePad, -artworkHeight / 2 - framePad, artworkWidth + framePad * 2, artworkHeight + framePad * 2);
    context.shadowColor = 'transparent';
    if (!isFramedPhoto) {
      context.fillStyle = '#fbfaf6';
      context.fillRect(-artworkWidth / 2 - 6, -artworkHeight / 2 - 6, artworkWidth + 12, artworkHeight + 12);
      const matHighlight = context.createLinearGradient(-artworkWidth / 2 - 6, -artworkHeight / 2 - 6, artworkWidth / 2 + 6, -artworkHeight / 2 + 20);
      matHighlight.addColorStop(0, 'rgba(255, 255, 255, 0.34)');
      matHighlight.addColorStop(0.54, 'rgba(255, 255, 255, 0.08)');
      matHighlight.addColorStop(1, 'rgba(255, 255, 255, 0)');
      context.fillStyle = matHighlight;
      context.fillRect(-artworkWidth / 2 - 6, -artworkHeight / 2 - 6, artworkWidth + 12, 26);
    }
    if (isFramedPhoto) {
      context.fillStyle = '#111711';
      context.fillRect(-artworkWidth / 2, -artworkHeight / 2, artworkWidth, artworkHeight);
      drawImageContained(-artworkWidth / 2, -artworkHeight / 2, artworkWidth, artworkHeight);
    } else {
      context.drawImage(image, -artworkWidth / 2, -artworkHeight / 2, artworkWidth, artworkHeight);
    }
    context.strokeStyle = isFramedPhoto ? 'rgba(18, 23, 18, 0.82)' : 'rgba(16, 32, 22, 0.2)';
    context.lineWidth = isFramedPhoto ? 4 : 2;
    context.strokeRect(-artworkWidth / 2, -artworkHeight / 2, artworkWidth, artworkHeight);
    if (isFramedPhoto) {
      const frameGlow = context.createLinearGradient(-artworkWidth / 2, -artworkHeight / 2, artworkWidth / 2, -artworkHeight / 2 + 34);
      frameGlow.addColorStop(0, 'rgba(255, 255, 255, 0.16)');
      frameGlow.addColorStop(0.48, 'rgba(255, 255, 255, 0.04)');
      frameGlow.addColorStop(1, 'rgba(255, 255, 255, 0)');
      context.fillStyle = frameGlow;
      context.fillRect(-artworkWidth / 2, -artworkHeight / 2, artworkWidth, 36);
      context.strokeStyle = 'rgba(0, 0, 0, 0.34)';
      context.lineWidth = 8;
      context.strokeRect(-artworkWidth / 2 - 4, -artworkHeight / 2 - 4, artworkWidth + 8, artworkHeight + 8);
    }
    context.fillStyle = 'rgba(255, 253, 248, 0.9)';
    context.strokeStyle = 'rgba(17, 25, 20, 0.32)';
    context.lineWidth = 2;
    context.beginPath();
    context.arc(0, -artworkHeight / 2 - framePad - 10, 6, 0, Math.PI * 2);
    context.fill();
    context.stroke();
    context.restore();

    context.save();
    context.fillStyle = 'rgba(0, 0, 0, 0.12)';
    context.beginPath();
    context.ellipse(centerX, wallAnchorY, artworkWidth * 0.42, 12, 0, 0, Math.PI * 2);
    context.fill();
    context.restore();

    const cardX = 70;
    const cardY = 1268;
    const cardWidth = 1060;
    const cardHeight = 268;
    const cardRadius = 28;
    const viewLabel = isFramedPhoto ? 'Framed room preview' : 'Painting-only room preview';
    const wallGuideLabel = wallGuideInches ? ` · ${wallGuideInches} in wall guide` : '';
    context.save();
    context.shadowColor = 'rgba(33, 54, 42, 0.18)';
    context.shadowBlur = 34;
    context.shadowOffsetY = 16;
    context.fillStyle = 'rgba(255, 253, 248, 0.94)';
    context.beginPath();
    context.roundRect(cardX, cardY, cardWidth, cardHeight, cardRadius);
    context.fill();
    context.restore();

    context.fillStyle = '#9c7627';
    context.font = '700 22px system-ui, sans-serif';
    context.fillText(viewLabel.toUpperCase(), 105, 1344);
    context.fillStyle = '#21362a';
    drawRoomPreviewLine(listing.title, 105, 1404, 990, 700, 'Georgia, serif', 42, 28);
    drawRoomPreviewLine(`${priceLine(listing)} · ${buyerDimensionPhrase(listing)} · Leslie Roberts`, 105, 1456, 990, 600, 'system-ui, sans-serif', 28, 21);
    context.fillStyle = '#667066';
    drawRoomPreviewLine(canUseLiveWall ? `Live wall preview from lesliespaintings.com${wallGuideLabel}` : `Room preview from lesliespaintings.com${wallGuideLabel}`, 105, 1496, 990, 500, 'system-ui, sans-serif', 24, 18);
    context.fillStyle = '#9c7627';
    drawRoomPreviewLine('Ask Leslie if this one feels right for your wall.', 105, 1534, 990, 700, 'system-ui, sans-serif', 24, 18);

    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((result) => {
        if (result) resolve(result);
        else reject(new Error('Room preview image could not be created.'));
      }, 'image/png');
    });
  }

  async function saveRoomPreviewImage() {
    if (!roomView || !roomDisplayImage) return;
    setRoomPreviewStatus('preparing');

    const listing = roomView.listing;

    try {
      const blob = await createRoomPreviewBlob();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = roomPreviewFileName(listing, roomView.mediaMode);
      link.href = url;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setRoomPreviewStatus('saved');
      // Keep the confirmation visible long enough for the mobile save sheet to close.
      window.setTimeout(() => setRoomPreviewStatus('idle'), 5000);
      window.setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch {
      setRoomPreviewStatus('error');
      window.setTimeout(() => setRoomPreviewStatus('idle'), 3200);
    }
  }

  function startRoomDrag(event: ReactPointerEvent<HTMLDivElement>) {
    if (!roomView) return;
    const stage = event.currentTarget.closest('.room-view-stage');
    if (!(stage instanceof HTMLElement)) return;
    const rect = stage.getBoundingClientRect();
    roomDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      baseX: roomView.x,
      baseY: roomView.y,
      width: rect.width,
      height: rect.height
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function moveRoomDrag(event: ReactPointerEvent<HTMLDivElement>) {
    const drag = roomDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const nextX = drag.baseX + ((event.clientX - drag.startX) / drag.width) * 100;
    const nextY = drag.baseY + ((event.clientY - drag.startY) / drag.height) * 100;
    updateRoomPlacement({
      x: clamp(nextX, 12, 88),
      y: clamp(nextY, 16, 84)
    });
  }

  function endRoomDrag(event: ReactPointerEvent<HTMLDivElement>) {
    if (roomDragRef.current?.pointerId === event.pointerId) {
      roomDragRef.current = null;
    }
  }

  function handleArtworkImageClick(listing: ListingView) {
    openDetails(listing);
  }

  useEffect(() => {
    if (roomView || currentSearchParams().has('preview')) return;

    const nextUrl = galleryShareUrl(activeFilter, activeAvailabilityId, query);
    if (!nextUrl || nextUrl === window.location.href) return;
    window.history.replaceState({}, '', nextUrl);
  }, [activeAvailabilityId, activeFilter, query, roomView]);

  useEffect(() => {
    if (!selected) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape' && !fullscreenView) {
        setSelected(null);
      }
    }

    window.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [fullscreenView, selected]);

  useEffect(() => {
    if (!fullscreenView) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setFullscreenView(null);
      }
    }

    window.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [fullscreenView]);

  useEffect(() => {
    if (!livingView) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setLivingView(null);
      }
    }

    window.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [livingView]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(hover: none), (pointer: coarse), (max-width: 820px)');
    const syncPreviewDevice = () => setIsWallPreviewDevice(mediaQuery.matches);

    syncPreviewDevice();
    mediaQuery.addEventListener('change', syncPreviewDevice);

    return () => mediaQuery.removeEventListener('change', syncPreviewDevice);
  }, []);

  useEffect(() => {
    if (!turnstileSiteKey || document.querySelector('script[data-turnstile-script="true"]')) return undefined;

    // Load Turnstile only when a site key is configured so the normal gallery
    // path stays light and dependency-free.
    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.dataset.turnstileScript = 'true';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  useEffect(() => {
    if (roomPreviewOpenedRef.current) return;

    const previewId = currentSearchParams().get('preview');
    if (!previewId) return;

    const previewListing = publicListings.find((listing) => listing.id === previewId);
    if (!previewListing) return;

    roomPreviewOpenedRef.current = true;
    const previewMode = currentSearchParams().get('view') === 'framed' ? 'framed' : 'artwork';
    openRoomView(previewListing, previewMode, roomPlacementFromParams());
  }, [listings]);

  useEffect(() => {
    if (!roomView) return;
    syncRoomPreviewUrl(roomView.listing, roomView.mediaMode, roomView);
  }, [roomView?.listing.id, roomView?.mediaMode, roomView?.rotation, roomView?.scale, roomView?.wallGuideInches, roomView?.x, roomView?.y]);

  useEffect(() => {
    if (!roomView || !hasKnownDimensions(roomView.listing)) {
      setRoomArSupport('unavailable');
      return;
    }

    let cancelled = false;
    let supportTimer = 0;
    let model: RoomArModelElement | null = null;

    setRoomArSupport('checking');
    setRoomArSessionStatus('idle');

    function syncArSupport() {
      if (cancelled || !model) return;
      if (model.canActivateAR) {
        setRoomArSupport('available');
      } else if (model.loaded) {
        setRoomArSupport('unavailable');
      }
    }

    function handleArStatus(event: Event) {
      const status = (event as CustomEvent<{ status?: string }>).detail?.status;
      if (status === 'object-placed') setRoomArSessionStatus('placed');
      if (status === 'session-started') setRoomArSessionStatus('starting');
      if (status === 'failed') setRoomArSessionStatus('failed');
      if (status === 'not-presenting') setRoomArSessionStatus('idle');
    }

    void import('@google/model-viewer')
      .then(async () => {
        await window.customElements.whenDefined('model-viewer');
        if (cancelled) return;
        model = roomArModelRef.current;
        if (!model) {
          setRoomArSupport('unavailable');
          return;
        }
        model.addEventListener('load', syncArSupport);
        model.addEventListener('ar-status', handleArStatus);
        syncArSupport();
        supportTimer = window.setTimeout(() => {
          if (!cancelled && !model?.canActivateAR) setRoomArSupport('unavailable');
        }, 2400);
      })
      .catch(() => {
        if (!cancelled) setRoomArSupport('unavailable');
      });

    return () => {
      cancelled = true;
      window.clearTimeout(supportTimer);
      model?.removeEventListener('load', syncArSupport);
      model?.removeEventListener('ar-status', handleArStatus);
    };
  }, [roomView?.listing.id, roomView?.mediaMode]);

  useEffect(() => {
    if (!roomView?.cameraRequested) return undefined;

    let active = true;
    let stream: MediaStream | null = null;

    async function startCamera() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraStatus('blocked');
        setCameraError('Camera preview is not available in this browser.');
        return;
      }

      setCameraStatus('starting');
      setCameraError('');

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        });

        if (!active) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        if (roomVideoRef.current) {
          roomVideoRef.current.srcObject = stream;
          await roomVideoRef.current.play();
        }

        setCameraStatus('live');
      } catch {
        if (!active) return;
        setCameraStatus('blocked');
        setCameraError('Camera access is off for this browser. Allow camera access to see the painting on your wall, or use the preview here to check the size and shape.');
      }
    }

    void startCamera();

    return () => {
      active = false;
      stream?.getTracks().forEach((track) => track.stop());
      if (roomVideoRef.current) {
        roomVideoRef.current.srcObject = null;
      }
    };
  }, [cameraRequestCount, roomView?.cameraRequested, roomView?.listing.id]);

  const selectedMedia = selected?.media[selectedMediaIndex] ?? selected?.media[0];
  const roomMetrics = roomView ? roomArtworkMetrics(roomView.listing, roomView.scale) : null;
  const roomFramePreview = roomView ? framedRoomPreview(roomView.listing) : undefined;
  const roomDisplayMedia = roomView?.mediaMode === 'framed' && roomFramePreview ? roomFramePreview : undefined;
  const roomDisplayImage = roomDisplayMedia?.src ?? roomView?.listing.image;
  const roomArModel = roomView ? roomArModelUrl(roomView.listing, roomView.mediaMode) : null;
  const roomScaleConfidence = roomView ? roomScaleConfidenceText(roomView.listing) : null;
  const roomSummary = roomView ? roomViewSummary(roomView.listing, roomView.mediaMode) : null;
  const roomVisitorSteps = roomView ? roomViewVisitorSteps(roomView.listing, roomView.mediaMode) : [];
  const roomTruthNote = roomView ? roomPreviewTruthNote(roomView.cameraRequested, cameraStatus, roomArSupport) : '';
  const roomWallFacts = roomView ? roomWallFitFacts(roomView.listing, roomView.mediaMode) : [];
  const roomModeFacts = roomView ? roomPreviewModeFacts(roomView.cameraRequested, cameraStatus, roomArSupport) : [];
  const roomWallGuide = roomView ? roomWallWidthGuide(roomView.listing, roomView.scale, roomView.wallGuideInches) : null;

  return (
    <div className="site-shell">
      <header className="topbar">
        <a className="brand" href="#top" aria-label="Leslie's Paintings home">
          <span className="brand-mark">
            <img src="/brand/leslies-paintings-mark-selected.png" alt="" />
          </span>
          <span>
            <strong>Leslie's Paintings</strong>
            <small>Leslie Roberts</small>
          </span>
        </a>
        <nav aria-label="Primary navigation">
          <a href="#available">Available</a>
          <a href="#about">About</a>
          <a href="#contact">Contact</a>
        </nav>
      </header>

      <main id="top">
        <section className="hero-section">
          <div className="hero-copy">
            <img
              className="hero-lockup"
              src="/brand/leslies-paintings-logo-selected.png"
              alt="Leslie's Paintings by Leslie Roberts logo with golden-hour Lowcountry marsh artwork"
              decoding="async"
            />
            <p className="eyebrow">Original Lowcountry marsh paintings</p>
            <h1>Peaceful Lowcountry originals in golden marsh light.</h1>
            <p className="hero-lede">
              I am Leslie Roberts, a pediatric nurse, wife, mother of four, and self-taught painter. I paint quiet marsh grass, wide skies, and water that feels like a deep breath.
            </p>
            <div className="hero-actions">
              <a className="button primary" href="#available">
                <ShoppingBag size={18} />
                View available originals
              </a>
              <a className="button secondary" href="#about">
                About Leslie
              </a>
            </div>
          </div>
        </section>

        <section className="section-heading">
          <p className="eyebrow">Available now</p>
          <div>
            <h2>Available original Lowcountry paintings</h2>
            <p>Take a quiet look first. Open any piece for the full painting, frame views when I have them, and the simple details.</p>
          </div>
        </section>

        <section className="collector-panel" id="available">
          <aside className="slow-looking-strip" aria-label="A quiet way to view the gallery">
            <span>Before choosing</span>
            <strong>Notice the sky, the water, and where your eye wants to rest.</strong>
            <p>Some pieces are soft and still. Some carry a little more sunset glow. The right one usually gives you a small pause.</p>
          </aside>
          <div className="filter-row" aria-label="Filter listings">
            <div className="search-field">
              <Search size={18} />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search title, mood, or location"
              />
            </div>
            <details className="gallery-filter-drawer" open={activeFilter !== 'All' || !['all', 'available'].includes(activeAvailabilityId)}>
              <summary>
                <Filter size={18} />
                <span>Search and filter</span>
              </summary>
              <div className="filter-pills">
                {filters.map((filter) => (
                  <button
                    className={filter === activeFilter ? 'active' : ''}
                    key={filter}
                    onClick={() => setActiveFilter(filter)}
                    type="button"
                  >
                    {filter}
                  </button>
                ))}
              </div>
              <div className="availability-pills" aria-label="Filter by availability">
                {primaryAvailabilityFilters.map((filter) => (
                  <button
                    className={filter.id === activeAvailabilityId ? 'active' : ''}
                    key={filter.id}
                    onClick={() => setActiveAvailabilityId(filter.id)}
                    type="button"
                    title={filter.note}
                  >
                    <span>{filter.label}</span>
                    <small>{filter.count}</small>
                  </button>
                ))}
              </div>
              <details className="status-filter-more">
                <summary>Past and held work</summary>
                <div className="availability-pills" aria-label="Filter by pending, reserved, sold, or not listed status">
                  {secondaryAvailabilityFilters.map((filter) => (
                    <button
                      className={filter.id === activeAvailabilityId ? 'active' : ''}
                      key={filter.id}
                      onClick={() => setActiveAvailabilityId(filter.id)}
                      type="button"
                      title={filter.note}
                    >
                      <span>{filter.label}</span>
                      <small>{filter.count}</small>
                    </button>
                  ))}
                </div>
              </details>
            </details>
          </div>

          <div className="listing-grid">
            {visibleListings.length ? (
              visibleListings.map((listing) => {
              const framePreview = framedPreview(listing);
              const sold = isSoldListing(listing);
              const pending = isPendingListing(listing);
              const reserved = isReservedListing(listing);
              const unavailable = isUnavailableListing(listing);
              const statusClass = availabilityClass(listing);
              return (
                <article className={['art-card', statusClass ? `${statusClass}-card` : ''].filter(Boolean).join(' ')} key={listing.id}>
                  <button
                    className={[
                      'art-image-button',
                      framePreview ? 'has-frame-preview' : '',
                      quickFrameId === listing.id ? 'showing-frame' : '',
                      unavailable ? 'is-unavailable' : ''
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onBlur={() => setQuickFrameId((current) => (current === listing.id ? null : current))}
                    onClick={() => handleArtworkImageClick(listing)}
                    type="button"
                    aria-label={
                      framePreview
                        ? `Open gallery for ${listing.title}; framed Marketplace preview appears on hover or first tap`
                        : `Open gallery for ${listing.title}`
                    }
                  >
                    <img className="art-card-primary-image" src={listing.image} alt={artAlt(listing)} loading="lazy" decoding="async" />
                    {framePreview ? (
                      <img className="art-card-frame-preview" src={framePreview.src} alt={mediaAlt(listing, framePreview)} loading="lazy" decoding="async" aria-hidden="true" />
                    ) : null}
                    <span className={['price-badge', statusClass].filter(Boolean).join(' ')}>{priceOrSold(listing)}</span>
                    {sold ? <em className="sold-badge">Sold</em> : null}
                    {pending ? <em className="pending-badge">Pending</em> : null}
                    {reserved ? <em className="reserved-badge">Reserved</em> : null}
                    {listing.media.length > 1 ? <em className="media-count">{listing.media.length} photos</em> : null}
                    {framePreview ? (
                      <em className="framed-badge">
                        <Frame size={14} />
                        {quickFrameId === listing.id ? 'Frame shown' : 'Frame quick look'}
                      </em>
                    ) : null}
                  </button>
                  <div className="art-card-body">
                    <div>
                      <h3>{listing.title}</h3>
                      <p>{dimensionLabel(listing)}</p>
                    </div>
                    <div className="card-actions">
                      <button
                        className="button quiet-card-action"
                        onClick={() => openDetails(listing)}
                        type="button"
                      >
                        <Images size={17} />
                        View
                      </button>
                      <button
                        className="icon-button"
                        data-testid={`room-view-card-${listing.id}`}
                        onClick={() => openRoomView(listing)}
                        type="button"
                        aria-label={`Preview ${listing.title} on your wall`}
                        title="Room view"
                      >
                        <Camera size={18} />
                      </button>
                    </div>
                  </div>
                </article>
              );
              })
            ) : (
              <section className="empty-gallery-state">
                <p className="eyebrow">Nothing in this view</p>
                <h3>No paintings match these filters right now.</h3>
                <p>Try Available originals or clear the search.</p>
                <div className="empty-gallery-actions">
                  <button className="button primary" onClick={() => setActiveAvailabilityId('available')} type="button">
                    Available originals
                  </button>
                  <button
                    className="button secondary"
                    onClick={() => {
                      setActiveAvailabilityId('all');
                      setActiveFilter('All');
                      setQuery('');
                    }}
                    type="button"
                  >
                    Clear filters
                  </button>
                </div>
              </section>
            )}
          </div>
        </section>

        <section className="about-section" id="about">
          <div className="about-copy">
            <p className="eyebrow">About Leslie</p>
            <h2>I am a self-taught artist drawn to wide skies and quiet marsh light.</h2>
            <p>
              My name is Leslie Roberts. I am a pediatric nurse, a wife, and a mother of four. Caring for others has always been at the heart of who I am. Painting became a quiet place for me - something I taught myself in the still hours between work and family life.
            </p>
            <p>
              My work focuses on light, space, and simplicity: the stillness of grass, shifting light on water, and a calm horizon that feels like a deep breath at the end of a long day.
            </p>
          </div>
        </section>

        <section className="contact-section" id="contact">
          <div className="contact-copy">
            <p className="eyebrow">Direct inquiries</p>
            <h2>Send me a note about a painting.</h2>
            <p>
              Tell me which piece caught your eye and what you would like to know. I will answer as soon as I can.
            </p>
            <div className="contact-actions">
              <a className="button secondary" href="https://www.facebook.com/marketplace/profile/764958396" target="_blank" rel="noreferrer">
                <Facebook size={18} />
                Marketplace
              </a>
              <a className="button secondary" href="https://www.instagram.com/lowcountrygallery/" target="_blank" rel="noreferrer">
                <Instagram size={18} />
                Instagram
              </a>
              <a className="button secondary" href="https://9-leslie-roberts.pixels.com" target="_blank" rel="noreferrer">
                <ArrowUpRight size={18} />
                Pixels prints
              </a>
            </div>
          </div>
          <form className="contact-form" onSubmit={submitContactForm}>
            {/* Spam trap: visually hidden from people, intentionally tempting to simple form bots. */}
            <label className="contact-honeypot" aria-hidden="true">
              Website
              <input
                autoComplete="off"
                name="website"
                tabIndex={-1}
                value={contactForm.website}
                onChange={(event) => updateContactField('website', event.target.value)}
              />
            </label>
            <div className="contact-form-grid">
              <label>
                <span>Your name</span>
                <input
                  autoComplete="name"
                  name="name"
                  required
                  type="text"
                  value={contactForm.name}
                  onChange={(event) => updateContactField('name', event.target.value)}
                />
              </label>
              <label>
                <span>Email</span>
                <input
                  autoComplete="email"
                  name="email"
                  required
                  type="email"
                  value={contactForm.email}
                  onChange={(event) => updateContactField('email', event.target.value)}
                />
              </label>
              <label>
                <span>Phone, if easier</span>
                <input
                  autoComplete="tel"
                  name="phone"
                  type="tel"
                  value={contactForm.phone}
                  onChange={(event) => updateContactField('phone', event.target.value)}
                />
              </label>
              <label>
                <span>Painting</span>
                <select name="artworkId" value={contactForm.artworkId} onChange={(event) => updateContactField('artworkId', event.target.value)}>
                  <option value="">I am not sure yet</option>
                  {availableListings.map((listing) => (
                    <option key={listing.id} value={listing.id}>
                      {listing.title} - {formatPrice(listing.price)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>What can I help with?</span>
                <select name="intent" value={contactForm.intent} onChange={(event) => updateContactField('intent', event.target.value)}>
                  {contactIntentOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Timing</span>
                <input
                  name="timeline"
                  placeholder="This week, gift date, project install..."
                  type="text"
                  value={contactForm.timeline}
                  onChange={(event) => updateContactField('timeline', event.target.value)}
                />
              </label>
              <label className="field-full">
                <span>Your note</span>
                <textarea
                  name="message"
                  required
                  rows={5}
                  value={contactForm.message}
                  onChange={(event) => updateContactField('message', event.target.value)}
                />
              </label>
            </div>
            {turnstileSiteKey ? <div className="cf-turnstile" data-sitekey={turnstileSiteKey} data-action="contact-form" /> : null}
            <div className="contact-form-footer">
              <button className="button primary" disabled={contactStatus === 'sending'} type="submit">
                {contactStatus === 'sending' ? <Sparkles size={18} /> : <Send size={18} />}
                {contactStatus === 'sending' ? 'Sending note' : 'Send note'}
              </button>
              <p className={contactStatus === 'error' ? 'form-status error' : 'form-status'} role="status">
                {contactFeedback || 'I will use your note only to answer your art question.'}
              </p>
            </div>
          </form>
        </section>
      </main>

      <footer>
        <span className="footer-brand">
          <img src="/brand/leslies-paintings-mark-selected.png" alt="" />
          Leslie's Paintings
        </span>
        <span>Original artwork by Leslie Roberts</span>
      </footer>

      {selected ? (
        <div className="detail-overlay" role="dialog" aria-modal="true" aria-label={selected.title}>
          <button className="overlay-close" onClick={() => setSelected(null)} type="button" aria-label="Close details">
            <X size={20} />
          </button>
          <article className="detail-panel">
            <div className="detail-media-column">
              <button className="detail-image detail-image-button" onClick={() => registerLivingTap(selected)} type="button" aria-label={`View ${selected.title}`}>
                {selectedMedia ? <img src={selectedMedia.src} alt={mediaAlt(selected, selectedMedia)} decoding="async" /> : <img src={selected.image} alt={artAlt(selected)} decoding="async" />}
                {selectedMedia?.kind === 'video' ? (
                  <span className="video-overlay">
                    <PlayCircle size={34} />
                    Marketplace video preview
                  </span>
                ) : null}
              </button>
              {selectedMedia && selectedMedia.kind !== 'video' ? (
                <button className="fullscreen-art-button" onClick={() => openFullscreenArt(selected, selectedMedia)} type="button">
                  <Eye size={18} />
                  View full screen
                </button>
              ) : null}
              {selected.media.length > 1 ? (
                <div className="media-thumbs" aria-label={`${selected.title} Marketplace media views`}>
                  {selected.media.map((media, index) => (
                    <button
                      className={index === selectedMediaIndex ? 'active' : ''}
                      key={`${media.src}-${index}`}
                      onClick={() => setSelectedMediaIndex(index)}
                      type="button"
                      aria-label={`Show ${mediaDisplayLabel(media)} for ${selected.title}`}
                    >
                      <img src={media.poster ?? media.src} alt="" loading="lazy" decoding="async" />
                      <span>{mediaDisplayLabel(media)}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="detail-copy">
              <p className="eyebrow">{availabilityText(selected)}</p>
              <h2>{selected.title}</h2>
              <p className={['detail-price', availabilityClass(selected)].filter(Boolean).join(' ')}>{priceLine(selected)}</p>
              <p>
                <MapPin size={16} />
                {selected.location}
                {` · ${dimensionLabel(selected)}`}
              </p>
              {!hasKnownDimensions(selected) ? <p className="size-coming-note">I will confirm the exact canvas size with you before pickup, delivery, or shipping.</p> : null}
              {isUnavailableListing(selected) ? <p className="sold-detail-note">{unavailablePrompt(selected)}</p> : selected.note ? <p>{selected.note}</p> : null}
              {selectedMedia?.note ? <p>{selectedMedia.note}</p> : null}
              {selected.media.some((media) => media.kind === 'framed') ? (
                <p className="framed-note">
                  <Frame size={16} />
                  <span>This listing includes Marketplace photos of my black-frame option.</span>
                </p>
              ) : null}
              <div className="detail-actions">
                <a
                  className="button primary"
                  href={isUnavailableListing(selected) ? 'https://www.facebook.com/marketplace/profile/764958396' : selected.listingUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Facebook size={18} />
                  {isUnavailableListing(selected) ? 'Message Leslie' : 'Open Marketplace listing'}
                </a>
                <button className="button secondary" data-testid="room-view-detail-button" onClick={() => openRoomView(selected, detailRoomViewMode(selectedMedia))} type="button">
                  <Camera size={18} />
                  {selectedMedia?.kind === 'framed' ? 'Try framed view' : 'Try in your room'}
                </button>
                {selected.pixelsUrl ? (
                  <a className="button secondary" href={selected.pixelsUrl} target="_blank" rel="noreferrer">
                    <Frame size={18} />
                    Print option
                  </a>
                ) : null}
              </div>
            </div>
          </article>
        </div>
      ) : null}

      {fullscreenView ? (
        <div className="fullscreen-art-overlay" role="dialog" aria-modal="true" aria-label={`Full screen view of ${fullscreenView.listing.title}`}>
          <button className="overlay-close fullscreen-art-close" onClick={closeFullscreenArt} type="button" aria-label="Close full screen view">
            <X size={20} />
          </button>
          <figure className="fullscreen-art-panel">
            <img src={fullscreenView.media.src} alt={mediaAlt(fullscreenView.listing, fullscreenView.media)} decoding="async" />
            <figcaption>
              <strong>{fullscreenView.listing.title}</strong>
              <span>
                {mediaDisplayLabel(fullscreenView.media)}
                {` · ${priceLine(fullscreenView.listing)} · ${dimensionLabel(fullscreenView.listing)}`}
              </span>
              <span>{availabilityText(fullscreenView.listing)}</span>
            </figcaption>
          </figure>
        </div>
      ) : null}

      {livingView ? (
        <div className="living-view-overlay" role="dialog" aria-modal="true" aria-label={`Living view for ${livingView.listing.title}`}>
          <button className="overlay-close living-close" onClick={closeLivingView} type="button" aria-label="Close living view">
            <X size={20} />
          </button>
          <section className="living-view-panel">
            <div className="living-view-art">
              <LivingArtworkView listing={livingView.listing} />
            </div>
            <div className="living-view-copy">
              <p className="eyebrow">Living view</p>
              <h2>{livingView.listing.title}</h2>
              <p>
                A quiet little version of the painting in motion: the sky, water, marsh grass, and light breathing softly without changing the heart of the piece.
              </p>
              <div className="living-view-meta">
                <span>{priceLine(livingView.listing)}</span>
                <span>{dimensionLabel(livingView.listing)}</span>
                <span>{livingView.listing.location}</span>
              </div>
              <div className="detail-actions">
                <a className="button primary" href={livingView.listing.listingUrl} target="_blank" rel="noreferrer">
                  <Facebook size={18} />
                  Ask me about it
                </a>
                <button className="button secondary" onClick={() => openDetails(livingView.listing)} type="button">
                  <Images size={18} />
                  Back to details
                </button>
              </div>
            </div>
          </section>
        </div>
      ) : null}

      {roomView ? (
        <div className="room-view-overlay" data-testid="room-view-overlay" role="dialog" aria-modal="true" aria-label={`Room view for ${roomView.listing.title}`}>
          <section className="room-view-panel">
            <header className="room-view-header">
              <span>
                <Camera size={18} />
                {isWallPreviewDevice ? 'Phone wall preview' : 'Live room view'}
              </span>
              <strong>{roomView.listing.title}</strong>
              <small>{`${priceLine(roomView.listing)} · ${dimensionLabel(roomView.listing)}`}</small>
              <button onClick={closeRoomView} type="button" aria-label="Close room view">
                <X size={19} />
              </button>
            </header>

            <div className="room-view-stage" onPointerDown={placeRoomArtwork}>
              <video ref={roomVideoRef} muted playsInline autoPlay />
              {roomArModel ? (
                <model-viewer
                  ref={(element) => {
                    roomArModelRef.current = element as RoomArModelElement | null;
                  }}
                  className="room-ar-model-loader"
                  src={roomArModel}
                  poster={roomDisplayImage}
                  alt={`True-scale wall model of ${roomView.listing.title} by Leslie Roberts`}
                  ar=""
                  ar-modes="webxr scene-viewer quick-look"
                  ar-placement="wall"
                  ar-scale="fixed"
                  camera-controls=""
                  loading="eager"
                  reveal="auto"
                  touch-action="none"
                />
              ) : null}
              <div className="room-view-live-guide" aria-hidden={cameraStatus !== 'live'}>
                <span>Simple camera preview</span>
                <strong>Tap once or drag the painting into place.</strong>
              </div>
              <div className="room-size-calibration" aria-live="polite">
                <Move size={14} />
                <span>{roomMetrics?.dimensionsKnown === false ? 'Size coming soon · shape preview only' : `${dimensionLabel(roomView.listing)} scale guide`}</span>
              </div>
              {cameraStatus !== 'live' ? (
                <div className="room-view-camera-state">
                  {roomArSupport === 'available' ? <ScanLine size={26} /> : <Camera size={26} />}
                  <div className="room-view-camera-copy">
                    <strong>
                      {cameraStatus === 'starting'
                        ? 'Starting simple preview'
                        : roomView.cameraRequested
                          ? 'Camera preview unavailable'
                          : roomArSupport === 'available'
                            ? 'Let your phone find the wall'
                            : roomArSupport === 'checking' && hasKnownDimensions(roomView.listing)
                              ? 'Preparing wall placement'
                              : 'Preview this piece on your wall'}
                    </strong>
                    {cameraError ? (
                      <span>{cameraError}</span>
                    ) : roomArSupport === 'available' ? (
                      <span>Move your phone slowly. It will detect a vertical surface and pin the painting there at its listed size.</span>
                    ) : (
                      <span>
                        {roomView.cameraRequested
                          ? 'Use the preview here, or try the camera again.'
                          : hasKnownDimensions(roomView.listing)
                            ? 'This device can still use the simple camera preview.'
                            : 'Add the exact canvas size before using true-scale wall placement.'}
                      </span>
                    )}
                  </div>
                  {cameraStatus !== 'starting' ? (
                    <div className="room-camera-actions">
                      {roomArSupport === 'available' ? (
                        <button
                          className="button primary room-native-ar-button"
                          data-testid="room-native-ar-button"
                          disabled={roomArSessionStatus === 'starting'}
                          onClick={startNativeWallPlacement}
                          type="button"
                        >
                          <ScanLine size={18} />
                          {roomArSessionStatus === 'starting' ? 'Finding wall' : roomArSessionStatus === 'failed' ? 'Try wall placement again' : 'Place it on my wall'}
                        </button>
                      ) : roomArSupport === 'checking' && hasKnownDimensions(roomView.listing) ? (
                        <button className="button primary room-native-ar-button" disabled type="button">
                          <ScanLine size={18} />
                          Preparing wall finder
                        </button>
                      ) : null}
                      <button
                        className={roomArSupport === 'available' || (roomArSupport === 'checking' && hasKnownDimensions(roomView.listing)) ? 'button secondary room-camera-button' : 'button primary room-camera-button'}
                        data-testid="room-preview-camera-button"
                        onClick={requestRoomCamera}
                        type="button"
                      >
                        <Camera size={18} />
                        {roomView.cameraRequested
                          ? 'Try camera again'
                          : roomArSupport === 'available' || (roomArSupport === 'checking' && hasKnownDimensions(roomView.listing))
                            ? 'Use simple preview'
                            : 'Start camera preview'}
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}
              {roomWallGuide ? (
                <span
                  className="room-wall-width-guide"
                  aria-hidden="true"
                  style={{
                    left: `${roomView.x}%`,
                    top: `${roomView.y}%`,
                    transform: `translate(-50%, -50%) rotate(${roomView.rotation}deg)`,
                    width: roomWallGuide.width
                  } as CSSProperties}
                >
                  <small>
                    <b>{roomWallGuide.label}</b>
                    <em>{roomWallGuide.note}</em>
                  </small>
                </span>
              ) : null}
              <span
                aria-hidden="true"
                className={['room-wall-contact-shadow', roomDisplayMedia ? 'is-framed-photo' : '', roomMetrics?.dimensionsKnown === false ? 'is-estimated-size' : ''].filter(Boolean).join(' ')}
                style={{
                  aspectRatio: roomMetrics?.aspectRatio,
                  left: `${roomView.x}%`,
                  top: `${roomView.y}%`,
                  width: roomMetrics?.displayWidth,
                  transform: `translate(-50%, -50%) rotate(${roomView.rotation}deg)`
                } as CSSProperties}
              />
              <div
                className={[
                  'room-artwork-frame',
                  roomMetrics?.sizeClass ?? '',
                  roomDisplayMedia ? 'is-framed-photo' : '',
                  roomMetrics?.dimensionsKnown === false ? 'is-estimated-size' : ''
                ]
                  .filter(Boolean)
                  .join(' ')}
                onPointerCancel={endRoomDrag}
                onPointerDown={startRoomDrag}
                onPointerMove={moveRoomDrag}
                onPointerUp={endRoomDrag}
                role="presentation"
                style={{
                  aspectRatio: roomMetrics?.aspectRatio,
                  left: `${roomView.x}%`,
                  top: `${roomView.y}%`,
                  width: roomMetrics?.displayWidth,
                  transform: `translate(-50%, -50%) rotate(${roomView.rotation}deg)`
                } as CSSProperties}
              >
                {cameraStatus === 'live' ? (
                  <span className="room-artwork-handle">
                    <Move size={13} />
                    Move
                  </span>
                ) : null}
                <span className="room-artwork-pin" aria-hidden="true" />
                <img data-testid="room-preview-image" src={roomDisplayImage} alt={roomDisplayMedia ? mediaAlt(roomView.listing, roomDisplayMedia) : artAlt(roomView.listing)} draggable={false} />
                <span className="room-artwork-source-badge">{roomDisplayMedia ? 'Framed view' : 'Painting only'}</span>
                <span className="room-artwork-anchor" aria-hidden="true" />
              </div>
            </div>

            <div className="room-view-controls">
              <div className={['room-scale-confidence', roomScaleConfidence?.level === 'estimated' ? 'estimated' : 'known'].join(' ')} role="note">
                <strong>{roomScaleConfidence?.title}</strong>
                <span>{roomScaleConfidence?.note}</span>
              </div>
              <div className="room-view-decision-card" role="note">
                <span>{roomSummary?.label}</span>
                <strong>{`${priceLine(roomView.listing)} · ${dimensionLabel(roomView.listing)}`}</strong>
                <small>{roomSummary?.note}</small>
                <em>{roomViewMessagePreview(roomView.listing, roomView.mediaMode)}</em>
              </div>
              <div className="room-view-action-row" aria-label="Room preview next actions">
                <div className="room-view-primary-action">
                  <span>Ready to ask about it?</span>
                  <button className="button primary room-view-message-button" onClick={messageLeslieFromRoomView} type="button">
                    <MessageCircle size={18} />
                    Message Leslie about this view
                  </button>
                  <small>{roomViewMessagePreview(roomView.listing, roomView.mediaMode)}</small>
                </div>
                <div className="room-view-secondary-actions" aria-label="Save or share this room preview">
                  <span>Second opinion tools</span>
                  <div>
                    <button className="button secondary" disabled={roomPreviewStatus === 'preparing'} onClick={copyRoomPreviewMessage} type="button">
                      {roomPreviewStatus === 'copied' ? <Check size={18} /> : <Copy size={18} />}
                      {roomPreviewStatus === 'copied' ? 'Copied message' : 'Copy message'}
                    </button>
                    <button className="button secondary" disabled={roomPreviewStatus === 'preparing'} onClick={shareRoomPreview} type="button">
                      <Share2 size={18} />
                      {roomPreviewStatus === 'preparing' ? 'Preparing' : roomPreviewStatus === 'shared' ? 'Shared' : 'Share preview'}
                    </button>
                    <button className="button secondary" disabled={roomPreviewStatus === 'preparing'} onClick={saveRoomPreviewImage} type="button">
                      {roomPreviewStatus === 'saved' ? <Check size={18} /> : <Images size={18} />}
                      {roomPreviewStatus === 'preparing' ? 'Preparing' : roomPreviewStatus === 'saved' ? 'Saved' : 'Save room image'}
                    </button>
                  </div>
                </div>
              </div>
              {roomFramePreview ? (
                <fieldset className="room-view-toggle" aria-label="Room preview image">
                  <legend>View</legend>
                  <button className={roomView.mediaMode === 'artwork' ? 'active' : ''} onClick={() => setRoomViewMode('artwork')} type="button">
                    Painting only
                  </button>
                  <button className={roomView.mediaMode === 'framed' ? 'active' : ''} onClick={() => setRoomViewMode('framed')} type="button">
                    Framed view
                  </button>
                </fieldset>
              ) : null}
              <label>
                <span>{roomMetrics?.dimensionsKnown === false ? 'Preview size' : 'Room scale'}</span>
                <input
                  type="range"
                  min="65"
                  max="135"
                  value={roomView.scale}
                  onChange={(event) => updateRoomPlacement({ scale: Number(event.target.value) })}
                />
              </label>
              <label>
                <span>Side to side</span>
                <input
                  type="range"
                  min="12"
                  max="88"
                  value={roomView.x}
                  onChange={(event) => updateRoomPlacement({ x: Number(event.target.value) })}
                />
              </label>
              <label>
                <span>Wall height</span>
                <input
                  type="range"
                  min="16"
                  max="84"
                  value={roomView.y}
                  onChange={(event) => updateRoomPlacement({ y: Number(event.target.value) })}
                />
              </label>
              <label>
                <span>Straighten</span>
                <input
                  type="range"
                  min="-12"
                  max="12"
                  value={roomView.rotation}
                  onChange={(event) => updateRoomPlacement({ rotation: Number(event.target.value) })}
                />
              </label>
              <fieldset className="room-placement-presets" aria-label="Room placement presets">
                <legend>Place it like</legend>
                {roomPlacementPresets.map((preset) => {
                  const isActive = Math.abs(roomView.scale - preset.scale) <= 2 && Math.abs(roomView.x - preset.x) <= 2 && Math.abs(roomView.y - preset.y) <= 2;
                  return (
                    <button className={isActive ? 'active' : ''} key={preset.id} onClick={() => applyRoomPlacementPreset(preset)} type="button">
                      {preset.label}
                    </button>
                  );
                })}
              </fieldset>
              <fieldset className="room-wall-guide-toggle" aria-label="Wall width guide">
                <legend>Compare to</legend>
                {roomWallGuideOptions.map((guideWidth) => (
                  <button
                    className={roomView.wallGuideInches === guideWidth ? 'active' : ''}
                    disabled={!hasKnownDimensions(roomView.listing)}
                    key={guideWidth}
                    onClick={() => updateRoomPlacement({ wallGuideInches: guideWidth })}
                    type="button"
                  >
                    {guideWidth} in
                  </button>
                ))}
                <button className={roomView.wallGuideInches === null ? 'active' : ''} onClick={() => updateRoomPlacement({ wallGuideInches: null })} type="button">
                  Off
                </button>
                <small>{roomWallGuideSummary(roomView.listing, roomView.wallGuideInches)}</small>
              </fieldset>
              <div className="room-scale-truth-note" role="note">
                <strong>{roomMetrics?.dimensionsKnown === false ? 'Shape only' : 'Scale guide'}</strong>
                <span>{roomScaleTruthSummary(roomView.listing, roomView.mediaMode, roomView.wallGuideInches)}</span>
              </div>
              <button className="button secondary" onClick={resetRoomViewPlacement} type="button">
                <RotateCcw size={18} />
                Reset
              </button>
              <details className="room-view-detail-drawer">
                <summary>
                  <span>
                    <strong>Room details</strong>
                    <small>Scale and privacy notes.</small>
                  </span>
                  <em>Show</em>
                </summary>
                <div className="room-wall-fit-facts" aria-label="Wall fit facts">
                  {roomWallFacts.map((fact) => (
                    <article key={fact.label}>
                      <span>{fact.label}</span>
                      <strong>{fact.value}</strong>
                      <small>{fact.note}</small>
                    </article>
                  ))}
                </div>
                <div className="room-view-step-strip" aria-label="Room preview steps">
                  {roomVisitorSteps.map((step, index) => (
                    <article key={step.title}>
                      <span>{index + 1}</span>
                      <strong>{step.title}</strong>
                      <small>{step.note}</small>
                    </article>
                  ))}
                </div>
                <div className="room-view-action-summary" role="note">
                  <strong>Save, share, or ask me.</strong>
                  <span>{roomViewActionSummary(roomView.listing, roomView.mediaMode)}</span>
                </div>
                <div className="room-view-truth-note" role="note">
                  <strong>{cameraStatus === 'live' ? 'Live room preview' : 'Private wall preview'}</strong>
                  <span>{roomTruthNote}</span>
                </div>
                <div className="room-preview-mode-facts" aria-label="Room preview behavior">
                  {roomModeFacts.map((fact) => (
                    <article key={fact.label}>
                      <span>{fact.label}</span>
                      <strong>{fact.value}</strong>
                      <small>{fact.note}</small>
                    </article>
                  ))}
                </div>
              </details>
              <span className={roomPreviewStatus === 'error' ? 'room-view-feedback error' : 'room-view-feedback'} role="status" aria-live="polite">
                {roomPreviewStatus === 'preparing'
                  ? 'Preparing a room preview image.'
                  : roomPreviewStatus === 'shared'
                  ? 'Room preview shared or copied with the link.'
                  : roomPreviewStatus === 'saved'
                    ? 'Room preview image saved for a second opinion.'
                    : roomPreviewStatus === 'copied'
                    ? 'Message copied with the painting title, price, size, and room preview link.'
                    : roomPreviewStatus === 'view'
                    ? `Now showing the ${roomView.mediaMode === 'framed' ? 'framed listing view' : 'painting-only view'}. The link in your address bar matches this view.`
                    : roomPreviewStatus === 'error'
                      ? 'The preview could not be saved. Try sharing it instead.'
                      : 'Ask Leslie starts a message. Share or Save keeps the room preview image for a second opinion.'}
              </span>
              <span className="room-view-fit-note">
                <Move size={15} />
                {roomMetrics?.label ?? dimensionLabel(roomView.listing)}
              </span>
              <span className="room-view-fit-note secondary-note">
                {roomFramePreview
                  ? roomView.mediaMode === 'framed'
                    ? 'Using the real framed listing photo. Ask Leslie to confirm whether the frame is included.'
                    : roomMetrics?.scaleLabel ?? 'Use the slider after you step closer or farther from the wall.'
                  : roomMetrics?.scaleLabel ?? 'Use the slider after you step closer or farther from the wall.'}
              </span>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
